import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { TIP_PAYMENT_METHOD, TipChoice, TipSettings, parseOrderTip, parseTipSettings, sameTipChoice, tipChoiceOf } from '../../shared/order-tip';
import { DELIVERY_SUBMIT_REFUSALS, parseOrderDeliveryFee } from '../../shared/order-delivery';
import { ActivatedRoute, Router } from '@angular/router';
import { CartService, CartItem, PromoCodeRefusal } from '../../services/cart.service';
import { ApiService, PaymentStatusResponse, ProductResponse } from '../../services/api.service';
import { Subscription } from 'rxjs';
import { OrderHistoryService, OrderHistoryItem } from '../../services/order-history.service';
import { NotificationService } from '../../services/notification.service';
import { OrderPayloadService, OrderMetadata } from '../../services/order-payload.service';
import { GuestPushService } from '../../services/guest-push.service';
import { fadeInOut, slideInOut, listAnimation, scaleInOut } from '../../shared/animations';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { LanguageService } from '../../services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FirestoreContextService } from '../../services/firestore-context.service';
import { AnalyticsService } from '../../services/analytics.service';
import { DeliveryFormData } from '../delivery-form/delivery-form.component';
import { CurrencyService } from '../../services/currency.service';
import { ShopStatusService } from '../../services/shop-status.service';
import { dineInPaymentMethods, hasHomeDelivery, hasInStorePickup } from '../../shared/ordering-eligibility';
import { PaymentSheetOption } from '../payment-method-sheet/payment-method-sheet.component';
import { commitLabelKey, paymentMethodCopy } from '../../shared/payment-method-display';
import { orderPaymentState, orderPaymentSummaryKey } from '../../shared/order-payment-display';
import { orderStatusKind, orderStatusLabelKey } from '../../shared/order-status';
import { recentOrders } from '../../services/order-history.service';
import { AwaitingPaymentOrder, AwaitingPaymentStore } from '../../services/awaiting-payment.store';
import { CartSupplement } from '../../models/supplement.model';
import { cartLineKey, cartLineTotal } from '../../shared/supplement-pricing';
import { SupplementCatalogService } from '../../services/supplement-catalog.service';
import { ITEM_NOTE_MAX_LENGTH, normalizeItemNote } from '../../shared/item-note';
import {
  expiredMessageKey,
  InitiationRefusal,
  isDeclineStillPayable,
  isRetryablePayment,
  notFoundOutcome,
  parseInitiationRefusal,
  PaymentPhase,
  paymentPhase,
  rememberedTransaction,
  unreachableOutcome,
  withinResumeWindow,
  parseReturnedOrderId,
  paymentAlert,
  shouldRetryPaymentStatus,
} from '../../shared/payment-outcome';
import { ProductSheetService } from '../../services/product-sheet.service';
import { EventMediaItem } from '../../models/event.model';

/**
 * The new-order push, captured at order time and replayed after payment.
 *
 * Serialised into localStorage, so it must stay plain data — see
 * stashPendingVenueNotification().
 */
interface VenueOrderNotification {
  orderId: number;
  tableId: string;
  clientId: string;
  imageUrl: string;
  regionName: string;
  regionId: string;
  tableName: string;
  orderOrigin: 'venue' | 'delivery' | 'pickup';
}

@Component({
    selector: 'app-order-details',
    templateUrl: './order-details.component.html',
    styleUrls: ['./order-details.component.scss'],
    animations: [
        fadeInOut,
        slideInOut,
        listAnimation,
        scaleInOut,
        // The before-order promotion sheet. iOS-style rather than the old centred card: it comes up
        // from the floor, which is where a sheet over a cart belongs, and the curve is the one the
        // rest of the app's sheets use.
        trigger('promoSheet', [
            transition(':enter', [
                style({ transform: 'translateY(102%)' }),
                animate('340ms cubic-bezier(.32,.72,0,1)', style({ transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('340ms cubic-bezier(.32,.72,0,1)', style({ transform: 'translateY(102%)' }))
            ])
        ]),
        trigger('promoScrim', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('280ms ease', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('280ms ease', style({ opacity: 0 }))
            ])
        ]),
        trigger('pageTransition', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('300ms ease-out', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('300ms ease-in', style({ opacity: 0 }))
            ])
        ]),
        trigger('slideDown', [
            state('hidden', style({
                transform: 'translateY(-100%)',
                opacity: 0,
                visibility: 'hidden'
            })),
            state('visible', style({
                transform: 'translateY(0)',
                opacity: 1,
                visibility: 'visible'
            })),
            transition('hidden => visible', [
                style({ visibility: 'visible' }),
                animate('300ms ease-out')
            ]),
            transition('visible => hidden', [
                animate('200ms ease-in'),
                style({ visibility: 'hidden' })
            ])
        ])
    ],
    standalone: false
})
export class OrderDetailsComponent implements OnInit, OnDestroy {
  cartItems: CartItem[] = [];
  total: number = 0;
  currencySymbol: string = '';
  clientData: any = null;
  tableId!: string | null;
  tableName: string | null = null;
  isNoteModalOpen: boolean = false;
  isSuccessModalOpen: boolean = false;
  isOrderDetailsDrawerOpen: boolean = false;
  orderNote: string = '';

  /*
   * The per-line note editor. One sheet for the whole list, aimed at a line by KEY rather than by
   * holding the CartItem: the cart re-emits fresh objects on every sync, and a held reference would
   * go stale while the sheet is open.
   */
  readonly itemNoteMaxLength = ITEM_NOTE_MAX_LENGTH;
  isItemNoteModalOpen = false;
  itemNoteDraft = '';
  /** Whether the line had a note when the sheet opened — Remove is offered only then. */
  itemNoteHadNote = false;
  /** Display name of the line being edited, for the sheet's title. */
  itemNoteLineName = '';
  private itemNoteLineKey: string | null = null;

  orderHistory: OrderHistoryItem[] = [];
  selectedOrder: OrderHistoryItem | null = null;
  /** i18n key for the drawer's "Paid online" / "Refunded" line, or null for silence. */
  selectedOrderPaymentKey: string | null = null;

  /**
   * The strip at the top of this screen, held to the same two-hour window as the
   * floating bar — otherwise the bar offers "1 previous order" and the list it opens is
   * empty, or vice versa.
   *
   * A getter rather than a stored field: the cutoff moves with the clock, and a value
   * computed once when history last changed would never let an order age out.
   */
  get recentOrderHistory(): OrderHistoryItem[] {
    return recentOrders(this.orderHistory, Date.now(), this.clientData?.id);
  }

  /** "Waiting for the venue" / "Accepted" / "Declined" for an order, or null for no badge. */
  orderStatusKey(order: OrderHistoryItem | null | undefined): string | null {
    const kind = orderStatusKind(order?.status);
    return kind ? orderStatusLabelKey(kind) : null;
  }

  /** The badge's colour class: the kind itself (`waiting` / `accepted` / `declined`). */
  orderStatusClass(order: OrderHistoryItem | null | undefined): string {
    return orderStatusKind(order?.status) ?? '';
  }

  /**
   * Re-reads the status of this venue's recent orders, so the list says what the venue did.
   *
   * Only orders still waiting (or never read) are asked about: an accepted or declined order does
   * not change back, and a refund — the one later change — is what the sheet's payment line is
   * for, read when it opens. `onlyId` is the order a push just named, asked about whatever it last
   * said. Failures are silent: the list keeps the last known status, which is never wrong for long.
   */
  private refreshRecentStatuses(onlyId?: number): void {
    const shopId = this.clientData?.id;
    if (!shopId) return;

    const orders = this.recentOrderHistory.filter(order =>
      onlyId !== undefined ? order.id === onlyId : orderStatusKind(order.status) !== 'accepted'
        && orderStatusKind(order.status) !== 'declined'
    );

    for (const order of orders) {
      this.apiService.getOrder(shopId, order.id).subscribe({
        next: (fresh: any) => {
          if (typeof fresh?.status === 'string') {
            this.orderHistoryService.setStatus(order.id, fresh.status, shopId);
          }
        },
        error: () => { /* keep the last known status */ }
      });
    }
  }

  /**
   * `?showOrder=<id>` — an order notification was tapped (the push's `redirect`, carried here by
   * the splash on a cold start, or by the PUSH_NAVIGATE handler in an open tab). Opens that
   * order's sheet over this screen, then drops the param so a reload does not reopen it.
   *
   * A subscription, not the snapshot: a tap while this screen is already open only changes the
   * query string, and ngOnInit does not run again. An order this browser has no record of (placed
   * elsewhere, or over a day ago) just leaves the guest on this screen.
   */
  private watchShowOrderParam(): void {
    this.showOrderSub = this.route.queryParamMap.subscribe(params => {
      const orderId = Number(params.get('showOrder'));
      if (!orderId) return;

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { showOrder: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.viewOrderDetails(orderId);
    });
  }
  isLoading: boolean = false;
  comesFromSuccessModal: boolean = false;
  featuredProducts: ProductResponse[] = [];
  currentLang: string = '';
  productPromotion: any;
  showPromotion: boolean = false;

  /**
   * The before-order sheet's copy, open in its scrollable reading panel.
   *
   * Same treatment as the promotion screen and for the same reason: this sheet stacks its text up
   * from the floor inside an `overflow: hidden` column, so long copy used to climb over the media
   * and push the TITLE out of the frame with nothing to show for it. @see PromotionDetailsComponent.
   */
  promoTextExpanded = false;

  /** Whether the clamped copy is hiding anything. Measured, never counted. @see TextOverflowDirective. */
  promoTextTruncated = false;
  promotionLoading: boolean = false;

  /**
   * The promotion's still, wrapped in the shape `app-event-media` renders.
   *
   * SCREEN A IS IMAGE-ONLY and stays that way: a `before-order-promotion` document carries one
   * `imageUrl` and nothing else — no thumbnail, no media type, no orientation — so there is nothing
   * to build a gallery or a video out of. Wrapping it anyway buys the shimmer while it loads, the
   * broken-image fallback, and the decoded-size report below, none of which the bare `<img>` here
   * had.
   */
  promotionMedia: EventMediaItem | null = null;

  /**
   * How that still fills the stage. Starts at `cover` and is corrected once the browser reports the
   * real shape, because this document has no `orientation` field to read — a wide image would
   * otherwise be cropped down the middle, which is the whole reason the promotion screen letterboxes
   * landscape media.
   */
  promotionFit: 'cover' | 'contain' = 'cover';

  /** The venue chip over the promotion media. Same source as everywhere else: the splash session. */
  promotionShopName: string = '';
  promotionShopLogoUrl: string = '';
  comesFrom: string = 'suggestProductsCard'
  // isOrderingActive: boolean = true;
  // isOrderSupported: boolean = true;
  isOrderingUnavailableModalOpen: boolean = false;
  orderingType: 'dine-in' | 'off-site' | 'delivery' = 'dine-in';

  // Delivery/Pickup form properties
  deliveryData: any = {};
  isDeliveryFormOpen: boolean = false;
  pickupLocations: any[] = [];
  isAddressReadOnly: boolean = false; // True when locationId is present (predefined delivery location)
  deliveryFormData: DeliveryFormData | null = null; // Store form data when submitted

  // Dine-in confirmation + payment method
  isDineInConfirmOpen: boolean = false;
  selectedPaymentMethod: string = '';

  // Online payment (BORICA)
  /** True while we are asking the backend what actually happened at the gateway. */
  isConfirmingPayment: boolean = false;
  /**
   * CARDHOLDERINFO. Spec Table 2: "ATTENTION! E-merchant must display the value to
   * the cardholder." The issuer's ACS decides whether to send one, so it is usually
   * absent — but when it is there, showing it is an obligation, not a choice.
   */
  paymentCardholderInfo: string | null = null;
  /**
   * The order a failed payment can be re-attempted for, or null when it cannot.
   *
   * Only a DECLINE is retryable. The order is still held server-side and still
   * payable, so the guest gets a way back to it — without this they have an alert,
   * an empty cart (cleared before the redirect) and no route forward at all.
   */
  paymentRetryOrderId: number | null = null;

  // ── The outstanding-payment panel ───────────────────────────────────────
  //
  // A card order that has been placed and is waiting on, or has failed, payment.
  // While one exists the screen shows its items read-only above a banner explaining
  // where the payment got to — instead of the empty cart every failure used to land on.

  /** The snapshot, or null when there is no payment outstanding. */
  awaitingPayment: AwaitingPaymentOrder | null = null;

  /**
   * What the banner says. `none` means no panel at all.
   *
   * Named `panelPhase`, not `paymentPhase`, so it cannot be confused with the imported
   * pure function of that name that computes it.
   */
  panelPhase: PaymentPhase | 'none' = 'none';

  /** The i18n key under `orderDetails.payment.` for the banner's body. */
  panelMessageKey = '';

  /**
   * The same key without the `orderDetails.payment.` prefix.
   *
   * The store records a settled answer by the bare key, so "Start a new order" needs
   * this spelling to settle with — and re-deriving it by stripping the prefix would be
   * one more place for the two to drift.
   */
  private panelMessageKeyRaw = '';

  /** Substituted into the banner's message — the RC on a decline. */
  panelMessageParams: Record<string, string> | undefined;

  /** Whether *Pay again* / *Continue payment* is offered, and which one. */
  canPayAgain = false;
  canContinuePayment = false;

  // ── Promo codes ─────────────────────────────────────────────────────────
  //
  // ⚠️ Every name here is `promoCode*`, never `promo*`. `promo*` already belongs to the
  // marketing PROMOTION banner on this same component (productPromotion, promotionLoading,
  // promoTextExpanded, and ~25 `.promo-*` SCSS classes). The two features are unrelated and
  // a shared prefix would make both unreadable.

  /**
   * Whether this venue can take a promo code at all.
   *
   * ⚠️ Read from the SHOP PAYLOAD (`promo_codes_available`), known before the guest types
   * anything, and defaulting to FALSE. Gating on a 404 from validate instead would show the
   * field to every guest of every venue and only remove it once they had already tried a
   * code — and a 404 cannot tell "this venue runs no promotions" from "the feature is off
   * platform-wide".
   *
   * The 404 handler is kept as a backstop for the venue's last code lapsing mid-session.
   * Any other failure (a bad code, a network blip) leaves the section up: hiding it on an
   * ordinary error would strand a guest holding a perfectly good code.
   */
  promoCodeAvailable: boolean = false;

  /** What the guest has typed. Bound with ngModel; not the same as the APPLIED code. */
  promoCodeInput: string = '';

  /** The code the SERVER says is on the order, or null. Drives the applied/clear affordance. */
  appliedPromoCode: string | null = null;

  /** The discount the server reports, in currency units. 0 when no code is applied. */
  promoCodeDiscount: number = 0;

  /** True while a validate call is in flight. */
  promoCodeValidating: boolean = false;

  /**
   * The i18n key for a refusal, or '' when there is nothing to say.
   *
   * A KEY, never an English string — the same discipline as `panelMessageKey` above, and
   * for the same reason: this screen runs in 15 languages.
   */
  promoCodeErrorKey: string = '';

  /** Substituted into the message — the minimum order amount, when that is the reason. */
  promoCodeErrorParams: Record<string, string> | undefined;

  /**
   * Set when the SERVER refuses a code that was already applied, at submit time.
   *
   * ⚠️ This is a different situation from `promoCodeErrorKey` and must not be merged with
   * it. That one is a field error on a code the guest is trying out. This one means the
   * order has been RE-PRICED UPWARDS after they had already seen a lower total, so it needs
   * an explicit confirm-or-cancel rather than an inline message.
   */
  showPromoCodeRefusedConfirm: boolean = false;
  promoCodeRefusedKey: string = '';
  /** Substituted into the refusal message — the minimum, when that is the reason. */
  promoCodeRefusedParams: Record<string, string> | undefined;
  /** The corrected, higher total the guest is being asked to accept. */
  promoCodeRefusedTotal: number = 0;
  /**
   * True when the server REFUSED to place the order over this, so the guest has a decision.
   * False when it was noticed mid-edit and the dialog is only telling them.
   */
  promoCodeRefusedBlockedSubmit: boolean = false;

  /**
   * The cart has edits the server has not priced yet, so the total on the bar is
   * provisional. The bar shows it rather than presenting a figure that may still move, and
   * the order button waits it out: ordering now would place the cart from BEFORE the edit.
   */
  isCartSyncing: boolean = false;

  /** Order was tapped and is waiting for the cart to sync before it goes on. */
  isSettlingCart: boolean = false;

  private promoCodeDebounce?: ReturnType<typeof setTimeout>;

  // Tips. The venue's options come from the shop payload; the amount and the selected choice
  // come from the SERVER's response to each sync (CartService.tip$), never from local maths.
  // Chosen in the dine-in payment sheet (app-tip-picker), not in the cart: tips are for
  // online payment only. The delivery/pickup form has its own picker and talks to
  // CartService directly; this component only shows the server's tip on the Order button.
  tipSettings: TipSettings = parseTipSettings(null);
  tipAmount: number = 0;
  selectedTip: TipChoice = { kind: 'none' };
  tipErrorKey: string = '';
  /** The row highlighted in the dine-in sheet — what decides whether the tip picker shows. */
  dineInHighlightedMethod: string = '';

  /**
   * The delivery fee on the order (our shipping line), as the server last reported it: null
   * for none, 0 for free delivery. Shown as a row above the total, like the tip.
   */
  deliveryFee: number | null = null;

  /**
   * Why the server refused the last submit as a delivery (`delivery_*`), handed to the
   * Order-now sheet so it can say so. Null otherwise.
   */
  deliverySubmitRefusal: string | null = null;
  private promoCodeSubs: Subscription[] = [];

  // Availability
  showUnavailableAlert: boolean = false;
  unavailableAlertMessage: string = '';
  showUnavailableItemsConfirm: boolean = false;
  unavailableItemNames: string[] = [];
  unavailableItemProductIds: number[] = [];
  private unavailableSub?: Subscription;
  private orderStatusPushSub?: Subscription;
  private showOrderSub?: Subscription;
  /** Stops the payment poll's setTimeout from firing into a destroyed component. */
  private destroyed: boolean = false;
  private paymentPollTimer?: ReturnType<typeof setTimeout>;

  // Track loaded images by product id
  loadedImages: Set<number> = new Set();
  errorImages: Set<number> = new Set();

  /** The promo card, scrolled into view when the field takes focus. */
  @ViewChild('promoCodeCard') promoCodeCard?: ElementRef<HTMLElement>;

  constructor(
    private cartService: CartService,
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private orderHistoryService: OrderHistoryService,
    private notificationService: NotificationService,
    private languageService: LanguageService,
    private orderPayloadService: OrderPayloadService,
    private afs: AngularFirestore,
    private fsContext: FirestoreContextService,
    private analytics: AnalyticsService,
    private currencyService: CurrencyService,
    private translate: TranslateService,
    private shopStatusService: ShopStatusService,
    private supplementCatalog: SupplementCatalogService,
    private productSheet: ProductSheetService,
    private awaitingPayments: AwaitingPaymentStore,
    private guestPushService: GuestPushService
  ) { }

  ngOnInit(): void {
    const cachedFeatured = sessionStorage.getItem('featuredProducts');

    if (cachedFeatured) {
      this.featuredProducts = JSON.parse(cachedFeatured);
    }

    // Older browsers have no visualViewport; the promo field simply gets no lift there.
    window.visualViewport?.addEventListener('resize', this.onViewportResize);
    window.visualViewport?.addEventListener('scroll', this.onViewportResize);
    this.cartService.cartItems$.subscribe(items => {
      this.cartItems = items;
    });

    this.cartService.total$.subscribe(total => {
      this.total = total;
    });

    // The applied code and its discount come from the SERVER's response to each sync, not
    // from anything computed here — see CartService.applyServerOrderTotals().
    this.promoCodeSubs.push(
      this.cartService.appliedPromoCode$.subscribe(code => {
        this.appliedPromoCode = code;
        if (code) {
          // Keep the box showing what is actually on the order, including after a reload.
          this.promoCodeInput = code;
          this.promoCodeErrorKey = '';
          this.promoCodeErrorParams = undefined;
        }
      }),
      this.cartService.syncing$.subscribe(syncing => {
        this.isCartSyncing = syncing;
      }),
      this.cartService.discountTotal$.subscribe(discount => {
        this.promoCodeDiscount = discount;
      }),
      // A code that was valid when applied and is not any more. The cart total has already
      // been corrected upwards by the time this fires.
      this.cartService.promoCodeRefused$.subscribe(refusal => {
        this.onPromoCodeRefused(refusal);
      }),
      this.cartService.tip$.subscribe(tip => {
        this.tipAmount = tip?.amount ?? 0;
        this.selectedTip = tipChoiceOf(tip);
        if (tip) {
          this.tipErrorKey = '';
        }
      }),
      // The venue no longer offers what was picked (it changed its presets mid-order). The
      // cart has already been re-synced without the tip.
      this.cartService.tipRefused$.subscribe(code => {
        this.tipErrorKey = code === 'tip_above_maximum' ? 'aboveMaximum' : 'notAvailable';
      }),
      this.cartService.deliveryFee$.subscribe(fee => {
        this.deliveryFee = fee;
      })
    );

    this.orderHistoryService.orderHistory$.subscribe(history => {
      this.orderHistory = history;
      // A status read lands as a NEW record, so the open sheet swaps to it to show the badge.
      const shown = this.selectedOrder;
      if (shown) {
        this.selectedOrder = history.find(order => order.id === shown.id && order.shopId === shown.shopId) ?? shown;
      }
    });

    this.currencySymbol = sessionStorage.getItem('currencySymbol') || '';
    const clientUnparsed = sessionStorage.getItem('clientData');
    if (clientUnparsed) {
      this.clientData = JSON.parse(clientUnparsed);
      // The venue's own answer, decided server-side and known before the guest types
      // anything. `=== true` on purpose: a payload from a backend that predates the key
      // must read as "no field", never as "maybe".
      this.promoCodeAvailable = this.clientData?.promo_codes_available === true;
      this.tipSettings = parseTipSettings(this.clientData);
      // Check ordering availability
      // this.isOrderingActive = this.clientData?.is_ordering_active !== false;
    }
    this.tableId = sessionStorage.getItem('tableId');

    // Get ordering type from sessionStorage
    const orderingType = sessionStorage.getItem('orderingType') as 'dine-in' | 'off-site' | 'delivery' | null;
    this.orderingType = orderingType || 'dine-in';

    const tableIdentifier = sessionStorage.getItem('tableIdentifier');
    if (this.orderingType === 'dine-in' && tableIdentifier) {
      this.getTableRegionInfo(tableIdentifier)
        .then(info => { this.tableName = info.tableName ?? null; })
        .catch(() => { /* leave tableName null; template falls back to tableId */ });
    }

    // Check if locationId is present (predefined delivery location)
    const locationId = sessionStorage.getItem('locationId');
    if (locationId) {
      this.isAddressReadOnly = true;
      // Load address from API immediately when locationId is present
      this.loadLocationAddress(locationId);
    }

    // Load pickup locations if available (for delivery type URLs)
    if (this.orderingType === 'delivery' || this.orderingType === 'off-site') {
      this.loadPickupLocations();
    }

    this.languageService.currentLang$.subscribe(lang => {
      this.currentLang = lang;
    });

    this.unavailableSub = this.cartService.unavailableProduct$.subscribe(event => {
      if (event.from && event.to) {
        this.unavailableAlertMessage = this.translate.instant('menu.availability.availableFromTo', { from: event.from, to: event.to });
      } else {
        this.unavailableAlertMessage = this.translate.instant('menu.availability.notAvailableNow');
      }
      this.showUnavailableAlert = true;
    });

    // Coming back from the BORICA page? Find out what actually happened before
    // anything else on this screen decides what to show.
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.resumePaymentFromRedirect();

    // What the venue did with the guest's orders: read now, again on each return to the tab
    // (onVisibilityChange), and for the order a push names when one arrives with the page open.
    this.refreshRecentStatuses();
    this.orderStatusPushSub = this.guestPushService.orderStatusPush$.subscribe(orderId => {
      this.refreshRecentStatuses(orderId);
      if (this.isOrderDetailsDrawerOpen && this.selectedOrder?.id === orderId) {
        this.loadPaymentSummary(orderId);
      }
    });
    this.watchShowOrderParam();

    const productPromotion = localStorage.getItem('beforeOrderPromotion');
    if (productPromotion) {
      this.productPromotion = JSON.parse(productPromotion);
      this.preparePromotionMedia();
      // Check if user closed promo today
      const promoId = this.productPromotion.id;
      const today = new Date().toISOString().slice(0, 10);
      const promoKey = `promoClosed_${promoId}_${today}`;
      if (!localStorage.getItem(promoKey)) {
        this.checkAndShowPromotion();
      }
    }
    // console.log(this.productPromotion);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.paymentPollTimer) {
      clearTimeout(this.paymentPollTimer);
    }
    this.unavailableSub?.unsubscribe();
    this.orderStatusPushSub?.unsubscribe();
    this.showOrderSub?.unsubscribe();
    this.promoCodeSubs.forEach(sub => sub.unsubscribe());
    if (this.promoCodeDebounce) {
      clearTimeout(this.promoCodeDebounce);
    }

    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.visualViewport?.removeEventListener('resize', this.onViewportResize);
    window.visualViewport?.removeEventListener('scroll', this.onViewportResize);
  }

  /*
   * ── Coming back without a lifecycle hook ────────────────────────────────
   *
   * The exit to BORICA is a full-page `window.location.href`, so this document goes
   * into the back/forward cache rather than being destroyed. Returning to it fires NO
   * Angular hook: ngOnInit does not run, and the component is exactly as it was left —
   * cart cleared, `isLoading` still true, and the fixed full-viewport loading overlay
   * (z-index 1000) swallowing every tap. A black screen with no way out.
   *
   * ⚠️ Note the guest reaches this by pressing Back TWICE. The first press lands on the
   * dashboard's auto-submitting interstitial, not here — that page has its own
   * pageshow guard so it stops re-submitting and offers a link back.
   *
   * visibilitychange covers the same shape for a phone locked mid-poll.
   */
  private onPageShow = (event: PageTransitionEvent): void => {
    if (!event.persisted) {
      return;
    }
    this.recoverAfterReentry();
  };

  private onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    // A push that arrived while the tab was hidden went to the service worker, not to us.
    this.refreshRecentStatuses();
    this.recoverAfterReentry();
  };

  private recoverAfterReentry(): void {
    if (this.destroyed) {
      return;
    }

    const shopId = this.awaitingPayment?.shopId ?? this.clientData?.id;
    const record = shopId ? this.awaitingPayments.read(shopId) : null;

    if (!record) {
      /*
       * ⚠️ Nothing is unstuck here, deliberately.
       *
       * visibilitychange fires on every ordinary return to the tab, including one
       * where `isLoading` is legitimately true because an order submission is still
       * in flight. Clearing it there would re-enable "Order now" mid-request and
       * invite a double submission. Only the payment round trip — which always leaves
       * a record behind — may reset it.
       */
      return;
    }

    // Now it is safe: we left this screen for BORICA with the overlay up, and the
    // bfcache restored it exactly as it was, spinner and all.
    this.isLoading = false;

    this.awaitingPayment = record;

    if (record.settled) {
      this.applyPanel(record.settled.phase, record.settled.messageKey);
      return;
    }

    // Do not stack polls: one may already be in flight from the original boot.
    if (this.isConfirmingPayment) {
      return;
    }

    // Paint first, for the reason resumePaymentFromRedirect() gives — and only when
    // there is nothing on the panel yet, so a re-entry never overwrites an answer the
    // original boot already reached.
    if (this.panelPhase === 'none') {
      this.showOutstandingPanel('confirming', 'stillProcessing');
    }

    // One round trip. The panel is already up; a burst here would only delay a repaint
    // the guest can ask for themselves with "Check again".
    this.isConfirmingPayment = true;
    this.pollPaymentStatus(record.shopId, record.orderId, 0, { thorough: false });
  }

  checkAndShowPromotion(): void {
    if (!this.productPromotion || !this.productPromotion.id) {
      this.showPromotion = false;
      return;
    }

    const promoId = this.productPromotion.id;
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const promoKey = `promoShown_${promoId}_${today}`;
    const lastPromoId = localStorage.getItem('lastPromoId');
    const lastPromoDate = localStorage.getItem('lastPromoDate');

    // If the promotion id or date is different, show the promotion
    if (lastPromoId !== String(promoId) || lastPromoDate !== today) {
      this.showPromotion = true;
      this.promoTextExpanded = false;
      this.promoTextTruncated = false;
      localStorage.setItem('lastPromoId', String(promoId));
      localStorage.setItem('lastPromoDate', today);
      localStorage.setItem(promoKey, 'true');

      // Track promotion presented event
      const orderId = this.cartService.getCurrentOrderId();
      const itemCount = this.cartItems.reduce((count, item) => count + item.quantity, 0);

      // Determine promotion type - default to 'product' if not specified
      const promotionType = this.productPromotion.type || 'product';

      this.analytics.log('pre_order_promotion_presented', {
        id: promoId,
        type: promotionType,
        order_id: orderId,
        item_count: itemCount
      });
    } else {
      this.showPromotion = false;
    }
  }

  /**
   * Identity of a cart ROW: the product plus its exact add-on selection, never the product id.
   *
   * One dish can now occupy several rows ("Pizza + cheese" and "Pizza + bacon"), and those rows
   * share an id. Tracking by id would let Angular reuse one row's DOM for the other whenever the
   * list re-emits, leaving a stale add-on summary and a stale stepper value on screen.
   *
   * Goes through the pure `cartLineKey` rather than `cartService.getLineKey` on purpose: ngFor
   * stores the function and calls it unbound, so `this` inside is the differ, not the component.
   */
  trackByLineKey(_index: number, item: CartItem): string {
    return cartLineKey(item.id, item.supplements);
  }

  /**
   * Stepper on one row.
   *
   * Addresses the LINE, not the product: `cartService.updateQuantity(productId, …)` is inert as
   * soon as the product occupies two rows (an absolute quantity must not be retargeted at a line
   * the guest did not point at), so a guest with two combinations of the same dish would tap +/−
   * and watch nothing happen.
   */
  updateQuantity(item: CartItem, change: number): void {
    const lineKey = this.cartService.getLineKey(item);
    const newQuantity = item.quantity + change;
    if (newQuantity > 0) {
      this.cartService.updateLineQuantity(lineKey, newQuantity, 'order_details');
    } else {
      // Same `remove_source` the row reported before add-ons existed, so the analytics series is
      // continuous; only the target narrows from "every line of this product" to this one line.
      this.cartService.removeLine(lineKey, this.comesFrom);
    }
  }

  /** The row's add-ons, or none. Templates iterate this instead of re-checking for null. */
  lineSupplements(item: CartItem): CartSupplement[] {
    return item.supplements ?? [];
  }

  hasSupplements(item: CartItem): boolean {
    return (item.supplements?.length ?? 0) > 0;
  }

  /**
   * One add-on as the guest reads it: `+ Extra cheese ×2`.
   *
   * `×qty` is printed rather than left implicit BECAUSE the price beside it is the ROW total
   * (unit price × the add-on's own quantity) — without it a 2 € add-on taken twice reads as a
   * 4 € add-on. It is dropped at 1, where it would only be noise. iOS and Android print it for
   * exactly the same reason.
   *
   * The name is re-resolved from the menu catalogue on every read rather than taken off the line:
   * the line's `name` is a snapshot from the moment the guest ticked the box, so it follows
   * neither a language switch nor a rename — and the cart is precisely where a guest re-reads
   * their order after switching language.
   */
  supplementLabel(supplement: CartSupplement): string {
    const name = this.supplementCatalog.localizedSupplementName(supplement.id, supplement.name ?? '');
    return supplement.quantity > 1 ? `+ ${name} ×${supplement.quantity}` : `+ ${name}`;
  }

  /** ROW total for one add-on: its unit price times its own quantity, not the line's. */
  supplementRowTotal(supplement: CartSupplement): number {
    return supplement.price * (supplement.quantity > 0 ? supplement.quantity : 1);
  }

  /**
   * Full money for the row, add-ons included.
   *
   * Rendered as an EXTRA line beneath the unit price rather than replacing it: the unit price
   * stays the base price so the two figures cannot double-count, which is the same split the
   * payload uses (`price` is the product alone, add-ons are priced beside it).
   */
  lineTotal(item: CartItem): number {
    return cartLineTotal(item.price, item.quantity, item.supplements);
  }

  /**
   * Whether this row's text column is a tap target that opens the product sheet on this line.
   *
   * Two guards, both of which have to hold:
   *  - the product must still be resolvable from the menu catalogue. A line whose dish has left
   *    the menu — or a cart restored from localStorage on a cold open, before the menu has
   *    loaded — has nothing to show, and opening an empty sheet is worse than not offering it.
   *  - ordering must be enabled. Add-ons stay READABLE on a browse-only menu ("Extra cheese +50"
   *    is menu information), they are simply not editable there.
   */
  canEditLine(item: CartItem): boolean {
    // A snapshot of an order that is already closed server-side is not editable —
    // tap-to-edit would otherwise rewrite a line on an order the guest has been asked
    // to pay for. This one gate covers the title's role/tabindex/click and the
    // supplement rows, which all read it.
    if (this.readOnlyItems) {
      return false;
    }

    return this.canEnableOrdering() && !!this.supplementCatalog.product(item.id);
  }

  /**
   * Open the product sheet on THIS line, not merely on this product — the sheet has to land on
   * the guest's exact combination, otherwise editing "Pizza + bacon" would silently rewrite
   * "Pizza + cheese".
   */
  openLineEditor(item: CartItem): void {
    if (!this.canEditLine(item)) return;
    this.productSheet.open(item.id, this.cartService.getLineKey(item));
  }

  private trackCartClosed(): void {
    const orderId = this.cartService.getCurrentOrderId();
    const itemCount = this.cartItems.reduce((count, item) => count + item.quantity, 0);

    this.analytics.log('cart_closed', {
      order_id: orderId,
      item_count: itemCount
    });
  }

  goBack(): void {
    this.trackCartClosed();
    this.router.navigate(['/menu']);
  }

  private async getTableRegionInfo(tableIdentifier: string): Promise<{ regionId?: string, regionName?: string, id?: string, tableName?: string }> {
    const shopId = sessionStorage.getItem('clientId');
    if (!shopId) return {};

    const snapshot = await this.fsContext.run(() =>
      this.afs.collection('tables', ref =>
        ref.where('businessId', '==', parseInt(shopId))
          .where('id', '==', tableIdentifier)
      ).get()
    ).toPromise();

    if (!snapshot || snapshot.empty) return {};

    const tableData = snapshot.docs[0].data() as any;
    return {
      regionId: tableData.regionId,
      regionName: tableData.regionName,
      id: tableData.id,
      tableName: tableData.name
    };
  }

  async finishOrder(): Promise<void> {
    // A second tap while the first is still waiting on the cart does nothing.
    if (this.isSettlingCart) return;

    this.isSettlingCart = true;
    try {
      if (!(await this.settleCartBeforeOrder())) return;
    } finally {
      this.isSettlingCart = false;
    }

    await this.refreshOrderingStatus();
    
    // Check new feature flags based on ordering type
    const canOrder = this.canEnableOrdering();
    if (!canOrder) {
      this.isOrderingUnavailableModalOpen = true;
      return;
    }
    
    // Check existing ordering validation.
    // is_order_supported applies to every ordering type. The fulfillment-method
    // requirement (in-store pickup or home delivery) only applies to off-site/
    // delivery orders — dine-in is ordered at the table and needs neither.
    const requiresFulfillmentMethod = this.orderingType === 'off-site' || this.orderingType === 'delivery';
    if (!this.clientData.is_order_supported ||
        (requiresFulfillmentMethod && !hasInStorePickup(this.clientData) && !hasHomeDelivery(this.clientData))) {
      this.isOrderingUnavailableModalOpen = true;
      return;
    }
    
    // For off-site and delivery orders, show form if not already filled
    if ((this.orderingType === 'off-site' || this.orderingType === 'delivery')) {
      this.openDeliveryForm();
      return;
    }

    /*
     * Dine-in: ask how they are paying, then place the order on that one tap.
     *
     * The sheet opens whenever the venue accepts ANYTHING at a table, including when
     * there is only one method. A single option is not a choice, but it is still a
     * sentence the guest needs — "Cash at the table / Pay in cash at the table" — and
     * for Pay now it is their last chance to stop before the order exists and the
     * browser leaves for BORICA's card form. Only a venue that takes nothing at a table
     * goes straight through.
     */
    const paymentMethods = this.getDineInPaymentMethods();

    if (paymentMethods.length >= 1) {
      this.isDineInConfirmOpen = true;
      // The sheet preselects its first option on open without telling us (see its
      // `selectedChange`), so the same first option is seeded here.
      this.onDineInMethodHighlighted(this.getDineInPaymentOptions()[0]?.method ?? '');
      return;
    }

    // Nothing is taken at the table, so there is no method to send and nothing to ask.
    await this.processOrder();
  }
  
  private canEnableOrdering(): boolean {
    // Rule 1: Table QR (dine-in)
    if (this.orderingType === 'dine-in') {
      return this.clientData?.feature_flags?.ft_orders_from_table === true && 
             this.isOrderingSupportedByExistingLogic();
    }
    
    // Rule 2: Main QR or Order Location (off-site/delivery)
    if (this.orderingType === 'off-site' || this.orderingType === 'delivery') {
      return this.clientData?.feature_flags?.ft_orders_from_home === true && 
             this.isOrderingSupportedByExistingLogic();
    }
    
    return false;
  }

  private isOrderingSupportedByExistingLogic(): boolean {
    // Match iOS isOrderingEnabled exactly: is_ordering_active must be true (nil/false → false), is_open_now/is_order_supported nil = OK.
    return this.clientData?.is_ordering_active === true &&
           (this.clientData?.is_open_now !== false) &&
           (this.clientData?.is_order_supported !== false);
  }

  /** What a guest at a table can pay with — see dineInPaymentMethods(). */
  getDineInPaymentMethods(): string[] {
    return dineInPaymentMethods(this.clientData);
  }

  /**
   * The options the dine-in sheet renders, in the order the venue configured them.
   *
   * Memoised on the method list, because this is bound as `[options]="..."` in the
   * template and therefore re-evaluated on every change-detection cycle. Returning a
   * fresh array each time makes Angular see the input as changed constantly, which is
   * both wasted work and a trap for any child that reacts to the change — the sheet
   * did, and it silently undid the guest's selection.
   */
  private dineInPaymentOptions: PaymentSheetOption[] = [];
  private dineInPaymentOptionsKey = '';

  getDineInPaymentOptions(): PaymentSheetOption[] {
    const methods = this.getDineInPaymentMethods();
    const key = methods.join('|');

    if (key !== this.dineInPaymentOptionsKey) {
      this.dineInPaymentOptionsKey = key;
      this.dineInPaymentOptions = methods.map((method) => {
        const copy = paymentMethodCopy(method, 'dineIn');
        return {
          method,
          labelKey: copy.titleKey,
          subtitleKey: copy.subtitleKey,
          commitLabelKey: commitLabelKey(method),
        };
      });
    }

    return this.dineInPaymentOptions;
  }

  /**
   * The sheet has committed a choice — place the order with it.
   *
   * Fires from the sheet's Order button, not from tapping a chip: highlighting one is
   * only a selection. `selectedPaymentMethod` is assigned before processOrder() rather
   * than bound, because processOrder() reads it synchronously to build orderMetadata.
   */
  onDineInPaymentChosen(method: string): void {
    this.selectedPaymentMethod = method;
    this.isDineInConfirmOpen = false;
    this.processOrder();
  }

  cancelDineInConfirm(): void {
    this.isDineInConfirmOpen = false;
    this.selectedPaymentMethod = '';
    // Leaving without ordering takes the tip back off. It was chosen for THIS payment, and
    // the Order button would otherwise go on adding it to a cart the guest walked away from.
    this.dineInHighlightedMethod = '';
    this.clearTip();
  }

  /** The tip picker shows under the rows only while Online payment is highlighted. */
  get showDineInTipPicker(): boolean {
    return this.tipSettings.enabled && this.dineInHighlightedMethod === TIP_PAYMENT_METHOD;
  }

  /**
   * The guest highlighted a row in the dine-in sheet (or it opened on its first one).
   *
   * Anything but Online payment clears the tip: the server would drop it from the order
   * anyway, and until then the Order button's total would still include it. This also
   * covers a tip left on the cart from before the sheet opened (an earlier visit, or the
   * old cart picker) when the sheet opens on Cash or Card.
   */
  onDineInMethodHighlighted(method: string): void {
    this.dineInHighlightedMethod = method;
    if (method !== TIP_PAYMENT_METHOD) {
      this.clearTip();
    }
  }

  /** Back to "No tip". Sends nothing unless a tip is actually on the order. */
  private clearTip(): void {
    this.tipErrorKey = '';
    if (this.selectedTip.kind !== 'none') {
      this.selectTip({ kind: 'none' });
    }
  }

  toggleNoteModal(): void {
    // Track note opened event when opening the modal
    if (!this.isNoteModalOpen) {
      const orderId = this.cartService.getCurrentOrderId();

      this.analytics.log('note_opened', {
        order_id: orderId
      });
    }

    this.isNoteModalOpen = !this.isNoteModalOpen;
  }

  saveNote(): void {
    const orderId = this.cartService.getCurrentOrderId();
    const previousNote = sessionStorage.getItem('orderNote') || '';

    // Track note events
    if (this.orderNote.trim() && !previousNote.trim()) {
      // Note added (first time)
      this.analytics.log('note_added', {
        order_id: orderId
      });
    } else if (this.orderNote.trim() && previousNote.trim() && this.orderNote !== previousNote) {
      this.analytics.log('note_changed', {
        order_id: orderId
      });
    }

    sessionStorage.setItem('orderNote', this.orderNote);
    this.isNoteModalOpen = false;
  }

  /**
   * Item notes are editable exactly where the order note is meant to be: on the live cart before
   * submit. Not on the read-only snapshot of an order awaiting payment — that order is closed
   * server-side and a line edit could not reach it — and never in the history drawer.
   */
  canEditItemNote(): boolean {
    return !this.readOnlyItems;
  }

  /** The line's note for display, or '' — tolerant of carts restored from older storage. */
  itemNote(item: CartItem): string {
    return normalizeItemNote(item?.note);
  }

  openItemNoteEditor(item: CartItem): void {
    if (!this.canEditItemNote()) return;
    this.itemNoteLineKey = this.cartService.getLineKey(item);
    this.itemNoteLineName = this.getProductName(item, this.currentLang);
    this.itemNoteDraft = this.itemNote(item);
    this.itemNoteHadNote = this.itemNoteDraft !== '';
    this.isItemNoteModalOpen = true;
  }

  closeItemNoteEditor(): void {
    this.isItemNoteModalOpen = false;
    this.itemNoteLineKey = null;
  }

  saveItemNote(): void {
    if (this.itemNoteLineKey) {
      this.cartService.setLineNote(this.itemNoteLineKey, this.itemNoteDraft);
    }
    this.closeItemNoteEditor();
  }

  removeItemNote(): void {
    if (this.itemNoteLineKey) {
      this.cartService.setLineNote(this.itemNoteLineKey, '');
    }
    this.closeItemNoteEditor();
  }

  addMoreClicked(): void {
    // Track add more clicked event
    const orderId = this.cartService.getCurrentOrderId();
    const itemCount = this.cartItems.reduce((count, item) => count + item.quantity, 0);

    this.analytics.log('add_more_clicked', {
      order_id: orderId,
      item_count: itemCount
    });

    // Navigate to menu
    this.router.navigate(['/menu']);
  }

  toggleSuccessModal(): void {
    this.isSuccessModalOpen = !this.isSuccessModalOpen;
    this.trackCartClosed();
    this.router.navigate(['/menu']);
  }

  toggleOrderDetailsDrawer(): void {
    this.isOrderDetailsDrawerOpen = !this.isOrderDetailsDrawerOpen;
    // Cleared on the way out so the next order cannot inherit this one's line.
    this.selectedOrderPaymentKey = null;
    if (this.comesFromSuccessModal) {
      this.isSuccessModalOpen = false;
      this.trackCartClosed();
      this.router.navigate(['/menu']);
    }
  }

  viewOrderDetails(orderId: number, comesFromSuccessModal: boolean = false): void {
    const order = this.orderHistoryService.getOrderById(orderId, this.clientData?.id);
    if (order) {
      this.selectedOrder = order;
      this.isOrderDetailsDrawerOpen = true;
      this.comesFromSuccessModal = comesFromSuccessModal;
      this.loadPaymentSummary(orderId);
    }
  }

  /**
   * "Paid online" / "Refunded" for a submitted order, or nothing.
   *
   * ⚠️ Fetched rather than read off the stored `OrderHistoryItem`, which carries no
   * payment fields at all: `set_paid` is sent as false on every order we create and is
   * flipped server-side once BORICA confirms, and a refund happens entirely after the
   * guest's last interaction. The local snapshot cannot know about either.
   *
   * A failure renders NOTHING — no spinner, no error. Most orders have nothing to say
   * about money, so silence is already the honest default here, and an error box would
   * be louder than the fact deserves.
   */
  private loadPaymentSummary(orderId: number): void {
    this.selectedOrderPaymentKey = null;

    const shopId = this.clientData?.id;
    if (!shopId) {
      return;
    }

    // The guest may close the drawer, or open a different order, while this is in
    // flight. Both outcomes below check that before touching anything.
    //
    // ⚠️ The ERROR path needs the guard just as much as the success path: opening order
    // A, closing it, then opening order B whose fetch succeeds, only for A's request to
    // fail afterwards, would otherwise wipe B's "Paid online" line off the screen.
    const stillOnScreen = () =>
      this.isOrderDetailsDrawerOpen && this.selectedOrder?.id === orderId;

    this.apiService.getOrder(shopId, orderId).subscribe({
      next: (order: any) => {
        // Recorded even if the guest has moved on: it is still this order's latest status.
        if (typeof order?.status === 'string') {
          this.orderHistoryService.setStatus(orderId, order.status, shopId);
        }

        if (!stillOnScreen()) {
          return;
        }

        this.selectedOrderPaymentKey = orderPaymentSummaryKey(
          orderPaymentState(order?.status, order?.set_paid, order?.refunded_at)
        );
      },
      error: () => {
        if (!stillOnScreen()) {
          return;
        }

        // Already null from the reset above; restated so a later edit cannot leave a
        // previous order's line standing over an order we failed to read.
        this.selectedOrderPaymentKey = null;
      },
    });
  }

  getProductName(product: any, _lang?: string): string {
    return this.languageService.localizeMetaValue(product.meta_data, '', product.name);
  }

  // Pre-order promotion title/description in the viewer's language. The dashboard
  // stores per-language maps (titleTranslations/descriptionTranslations) plus the
  // scalar title/description as the default-language fallback for older builds.
  /** The clamped copy reported whether it hides anything. Latches on — see the promotion screen. */
  onPromoTextOverflow(overflows: boolean): void {
    if (overflows) this.promoTextTruncated = true;
  }

  togglePromoText(): void {
    this.promoTextExpanded = !this.promoTextExpanded;
  }

  get promotionTitle(): string {
    if (!this.productPromotion) return '';
    return this.languageService.localizeMap(this.productPromotion.titleTranslations, this.productPromotion.title || '');
  }

  get promotionDescription(): string {
    if (!this.productPromotion) return '';
    return this.languageService.localizeMap(this.productPromotion.descriptionTranslations, this.productPromotion.description || '');
  }

  hasValidImage(item: any): boolean {
    return !!(item.image && item.image.trim() !== '');
  }

  getItemImageSrc(item: any): string {
    if (this.hasValidImage(item)) {
      return item.image;
    }
    return '../../../assets/ImageBroken.svg';
  }

  onImageLoad(itemId: number) {
    this.loadedImages.add(itemId);
  }

  onImageError(itemId: number) {
    this.errorImages.add(itemId);
  }

  isImageLoaded(itemId: number): boolean {
    return this.loadedImages.has(itemId);
  }

  isImageError(itemId: number): boolean {
    return this.errorImages.has(itemId);
  }

  /**
   * Wrap the promotion's single `imageUrl` as a media item, and read the venue for the chip.
   *
   * `mediaType` is left unset on purpose rather than spelled "image": `isVideoMedia` treats
   * anything that is not the literal string "video" as an image, so an absent type is already the
   * right answer, and inventing a value would imply this document has a field it does not.
   */
  private preparePromotionMedia(): void {
    const url = (this.productPromotion?.imageUrl ?? '').trim();
    this.promotionMedia = url ? { mediaUrl: url } : null;
    this.promotionFit = 'cover';
    this.promotionShopName = this.clientData?.name || '';
    this.promotionShopLogoUrl = this.clientData?.catalogue_icon || '';
  }

  /**
   * The still turned out to be wide, so letterbox it over its own blurred plate instead of cropping.
   *
   * Same rule the promotion screen applies from the `orientation` field; here it can only come from
   * the pixels, because this document has no such field.
   */
  onPromotionAspect(orientation: 'portrait' | 'landscape'): void {
    this.promotionFit = orientation === 'landscape' ? 'contain' : 'cover';
  }

  /** The blurred plate behind the still — the still itself. */
  get promotionBackdropUrl(): string | null {
    return (this.productPromotion?.imageUrl ?? '').trim() || null;
  }

  closePromotion(): void {
    this.showPromotion = false;
    // Optionally, set a flag in localStorage to not show again today
    if (this.productPromotion && this.productPromotion.id) {
      const promoId = this.productPromotion.id;
      const today = new Date().toISOString().slice(0, 10);
      const promoKey = `promoClosed_${promoId}_${today}`;
      localStorage.setItem(promoKey, 'true');
    }
  }

  addPromotionProductToOrder(): void {
    if (!this.productPromotion) return;

    // Track promotion action event
    const orderId = this.cartService.getCurrentOrderId();
    const itemCount = this.cartItems.reduce((count, item) => count + item.quantity, 0);

    // Determine promotion type - default to 'product' if not specified
    const promotionType = this.productPromotion.type || 'product';

    this.analytics.log('pre_order_promotion_action', {
      id: this.productPromotion.id,
      type: promotionType,
      order_id: orderId,
      item_count: itemCount
    });

    this.promotionLoading = true;

    const menuProductsRaw = sessionStorage.getItem('menuProducts');
    if (!menuProductsRaw) {
      this.promotionLoading = false;
      return;
    }

    const menuProducts = JSON.parse(menuProductsRaw);
    const product = menuProducts.find((p: any) => p.id === this.productPromotion.productId);
    if (!product) {
      this.promotionLoading = false;
      return;
    }

    this.cartService.addToCart(product, 'order');

    this.analytics.log('pre_order_promo_product_added', {
      product_id: String(product.id)
    });

    setTimeout(() => {
      this.promotionLoading = false;
      this.closePromotion();
    }, 600);
  }

  closeOrderingUnavailableModal(): void {
    this.isOrderingUnavailableModalOpen = false;
  }

  /**
   * Title/message for the ordering-unavailable modal.
   *
   * The modal is opened from two places: the shop-status checks below, and canEnableOrdering(),
   * which blocks on the ft_orders_from_table / ft_orders_from_home feature flags. A venue with
   * those flags off but a perfectly healthy status (is_order_supported / is_ordering_active /
   * is_open_now all true) matches none of the status cases, so the template's three branches all
   * rendered nothing and the modal showed a bare "Got it" button with no text. The final case is
   * an unconditional fallback so the modal can never come up empty again.
   */
  get orderingUnavailableCopy(): { title: string; message: string } {
    if (!this.clientData?.is_order_supported) {
      return {
        title: 'outsideOrderingUnavailable.title',
        message: 'outsideOrderingUnavailable.message'
      };
    }
    if (!this.clientData?.is_ordering_active) {
      return {
        title: 'orderDetails.orderingUnavailable.title',
        message: 'orderDetails.orderingUnavailable.message'
      };
    }
    if (this.clientData?.is_open_now === false) {
      return {
        title: 'orderDetails.orderingUnavailable.title',
        message: 'orderDetails.venueClosed.message'
      };
    }
    // Ordering is blocked by a feature flag rather than by shop status.
    return {
      title: 'orderDetails.orderingUnavailable.title',
      message: 'orderDetails.orderingUnavailable.message'
    };
  }

  private async refreshOrderingStatus(): Promise<void> {
    if (!this.clientData?.id) return;

    try {
      // Refresh shop status (is_ordering_active, is_open_now) before place order (align with iOS)
      const updated = await this.shopStatusService.refreshShopStatus(this.clientData.id);
      if (updated) {
        this.clientData = updated;
      }
    } catch (error) {
      console.error('Error refreshing ordering status:', error);
      // Keep existing value on error
    }
  }


  private async loadPickupLocations(): Promise<void> {
    // Load pickup locations from clientData.locations
    if (this.clientData?.locations && Array.isArray(this.clientData.locations)) {
      // Filter locations where is_pickup_location is true
      const pickupLocations = this.clientData.locations.filter(
        (loc: any) => loc.is_pickup_location === true
      );
      
      // Map to the format expected by the component (id and name)
      // Use id if available, otherwise use name as id (since it should be unique)
      this.pickupLocations = pickupLocations.map((loc: any) => ({
        id: loc.id || loc.name || loc.location_address_name,
        name: loc.name || loc.location_address_name,
        address: loc.location_address_name
      }));
    }
    
    // For delivery URLs with locationId, load location data and prefilled address
    if (this.orderingType === 'delivery') {
      const locationId = sessionStorage.getItem('locationId');
      if (locationId) {
        // If we already have pickup locations, don't override them
        // Otherwise, create a single location entry
        if (this.pickupLocations.length === 0) {
          this.pickupLocations = [{ id: locationId, name: 'Delivery Location' }];
        }
        // Load prefilled address from location data (only if address is not already set)
        if (!this.deliveryData?.address) {
          await this.loadLocationAddress(locationId);
        }
      }
    }
  }

  private async loadLocationAddress(locationId: string): Promise<void> {
    // Load address from REST API
    try {
      const locationData = await this.apiService.getOrderLocation(locationId).toPromise();
      if (locationData) {
        // Prefill address if available - this will be passed to the delivery form component
        this.deliveryData = locationData.data;
      } else {
        console.warn('locationData is null or undefined');
      }
    } catch (error) {
      console.error('Error loading location address:', error);
      // Don't fail if we can't load address - user can still enter it manually
    }
  }


  openDeliveryForm(): void {
    this.isDeliveryFormOpen = true;
    // If address is read-only but empty, ensure it's loaded
    if (this.isAddressReadOnly && !this.deliveryData?.address) {
      const locationId = sessionStorage.getItem('locationId');
      if (locationId) {
        this.loadLocationAddress(locationId);
      }
    }
  }

  closeDeliveryForm(): void {
    this.isDeliveryFormOpen = false;
  }

  onDeliveryFormSubmit(formData: DeliveryFormData): void {
    this.deliveryFormData = formData;
    this.deliverySubmitRefusal = null;
    this.isDeliveryFormOpen = false;
    // Proceed with order processing
    this.processOrder();
  }

  onDeliveryFormCancel(): void {
    this.deliverySubmitRefusal = null;
    this.isDeliveryFormOpen = false;
  }

  removeUnavailableItemsAndSubmit(): void {
    for (const productId of this.unavailableItemProductIds) {
      // `removeProduct`, not `removeFromCart`: availability is a property of the DISH, so every line
      // of it has to go — a dish held under two add-on selections is unavailable under both. Leaving
      // one behind would fail the very check below and re-open this modal on an endless loop.
      this.cartService.removeProduct(productId, 'order_details');
    }
    this.unavailableItemProductIds = [];
    this.unavailableItemNames = [];
    this.showUnavailableItemsConfirm = false;

    if (this.cartItems.length > 0) {
      this.processOrder();
    }
  }

  cancelUnavailableRemoval(): void {
    this.unavailableItemProductIds = [];
    this.unavailableItemNames = [];
    this.showUnavailableItemsConfirm = false;
  }

  // ── Promo codes ─────────────────────────────────────────────────────────

  /**
   * Lift the promo field clear of the on-screen keyboard.
   *
   * ⚠️ `window.innerHeight` does NOT change when the keyboard opens on iOS Safari — the
   * layout viewport stays put and only the VISUAL viewport shrinks. So the height comes
   * from `visualViewport`, and the listener is attached there rather than to `window`.
   *
   * The delay matches iOS's 0.35s: the scroll has to happen after the keyboard has
   * finished animating, or it centres the card against the pre-keyboard viewport and the
   * field ends up under it anyway.
   */
  onPromoCodeFocus(): void {
    setTimeout(() => {
      this.promoCodeCard?.nativeElement?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 350);
  }

  /** Keyboard height in px, 0 when closed. Drives the bar's lift and the scroll padding. */
  keyboardHeight: number = 0;

  private onViewportResize = (): void => {
    const vv = window.visualViewport;
    if (!vv) {
      return;
    }

    /*
     * What the keyboard covers: the layout viewport minus what is still visible below
     * the visual viewport's top. Clamped and thresholded because the visual viewport
     * also shrinks a few px for the URL bar, which is not a keyboard and must not move
     * the order bar.
     */
    const covered = window.innerHeight - vv.height - vv.offsetTop;
    const next = covered > 120 ? Math.round(covered) : 0;

    // No explicit change detection: zone.js patches EventTarget.addEventListener, and
    // visualViewport is an EventTarget, so this runs inside the zone already.
    if (next !== this.keyboardHeight) {
      this.keyboardHeight = next;
    }
  };

  /** True once there is something typed that is not already the applied code. */
  get canApplyPromoCode(): boolean {
    const typed = this.promoCodeInput.trim();
    return typed.length > 0 && typed.toUpperCase() !== (this.appliedPromoCode ?? '').toUpperCase();
  }

  /**
   * Preview the typed code as the guest types, without applying it.
   *
   * Debounced because the endpoint is throttled at 20/min per shop+IP — it is a code
   * oracle — and a keystroke-per-request would exhaust that inside one code.
   */
  onPromoCodeInput(): void {
    this.promoCodeErrorKey = '';
    this.promoCodeErrorParams = undefined;

    if (this.promoCodeDebounce) {
      clearTimeout(this.promoCodeDebounce);
    }

    const typed = this.promoCodeInput.trim();
    if (!typed) {
      return;
    }

    this.promoCodeDebounce = setTimeout(() => this.validatePromoCode(typed), 600);
  }

  /**
   * Ask the server what a code is worth. Preview only — nothing is applied by this.
   *
   * ⚠️ A 404 means the FEATURE is off for this venue, not that the code is bad. The whole
   * section is removed rather than showing an error: a guest cannot act on "promo codes do
   * not exist here", and an error box implying they typed something wrong is worse than
   * nothing. Prior art: cart.service.ts already special-cases 404 on a sync.
   */
  private validatePromoCode(code: string): void {
    if (!this.clientData?.id) return;

    this.promoCodeValidating = true;

    this.apiService.validatePromoCode(this.clientData.id, code, this.total).subscribe({
      next: (res) => {
        this.promoCodeValidating = false;

        if (res.valid) {
          this.promoCodeErrorKey = '';
          this.promoCodeErrorParams = undefined;
          return;
        }

        this.setPromoCodeError(res.reason, res.minimum_order_amount);
      },
      error: (err) => {
        this.promoCodeValidating = false;

        if (err?.status === 404) {
          this.promoCodeAvailable = false;
          return;
        }

        this.setPromoCodeError(undefined);
      }
    });
  }

  /**
   * Apply the typed code to the order.
   *
   * The server is what actually decides: this re-syncs the cart with the code attached and
   * the applied/discount state comes back from that response. Nothing is shown as applied
   * on the strength of the validate preview alone — the two can disagree, because the
   * preview is priced against the guest's own subtotal and the order is priced against the
   * server's.
   */
  applyPromoCode(): void {
    const typed = this.promoCodeInput.trim();
    if (!typed || !this.promoCodeAvailable) return;

    if (this.promoCodeDebounce) {
      clearTimeout(this.promoCodeDebounce);
    }

    this.promoCodeErrorKey = '';
    this.promoCodeErrorParams = undefined;
    this.cartService.applyPromoCode(typed);
  }

  /** Remove the applied code and return the order to gross. */
  clearPromoCode(): void {
    if (this.promoCodeDebounce) {
      clearTimeout(this.promoCodeDebounce);
    }

    this.promoCodeInput = '';
    this.promoCodeErrorKey = '';
    this.promoCodeErrorParams = undefined;
    this.cartService.applyPromoCode('');
  }

  /**
   * The server REFUSED to place the order because the applied code had died.
   *
   * Different from the mid-edit refusal below: nothing has been placed, so this is a real
   * decision rather than a notification. The corrected total comes off the 409 body, which
   * is the order the server would have placed, and the cart is updated to match so the
   * screen behind the dialog agrees with it.
   */
  private onPromoCodeRefusedAtSubmit(body: any): void {
    const minimum = parseFloat(body?.minimum_order_amount);
    this.setPromoCodeRefusal(body?.promo_code_refused, isNaN(minimum) ? undefined : minimum);

    const corrected = parseFloat(body?.total);
    if (!isNaN(corrected)) {
      this.promoCodeRefusedTotal = corrected;
      this.total = corrected;
    }

    // Read off the 409 rather than assumed gone: a code whose discount the venue LOWERED
    // (`promo_code_changed`) still applies, and the body says so. A refused code comes back
    // null with no discount.
    const discount = parseFloat(body?.discount_total);
    this.promoCodeDiscount = isNaN(discount) ? 0 : discount;
    this.appliedPromoCode = typeof body?.promo_code === 'string' && body.promo_code !== ''
      ? body.promo_code
      : null;
    this.promoCodeInput = this.appliedPromoCode ?? '';

    this.promoCodeRefusedBlockedSubmit = true;
    this.showPromoCodeRefusedConfirm = true;
  }

  /** "Order anyway" — the guest has seen the new price and accepted it. */
  confirmOrderAtCorrectedTotal(): void {
    this.showPromoCodeRefusedConfirm = false;
    this.promoCodeRefusedKey = '';
    // The refusal cleared the code server-side, so this is an ordinary submit.
    this.processOrder();
  }

  /** "Cancel" — back to the cart, nothing placed. */
  cancelOrderAtCorrectedTotal(): void {
    this.showPromoCodeRefusedConfirm = false;
    this.promoCodeRefusedKey = '';
    this.promoCodeRefusedBlockedSubmit = false;
  }

  /**
   * The server's total for an order response, or the fallback when it did not send one.
   *
   * Parsed at the boundary because the order endpoints send totals as STRINGS while
   * everything downstream of here does arithmetic on numbers.
   */
  private serverTotalOr(res: any, fallback: number): number {
    const parsed = parseFloat(res?.total);
    return isNaN(parsed) ? fallback : parsed;
  }

  /**
   * What a promo code took off this order, per the server. 0 when none did.
   *
   * Strings again, like `total` — `"1.33"`, not `1.33`. The validate endpoint returns
   * these as numbers, which is exactly why this parses rather than trusting the shape.
   */
  /**
   * A choice from the tip picker: "No tip", one of the venue's percentages, or an amount
   * the picker has already checked against the venue's maximum.
   */
  selectTip(choice: TipChoice): void {
    this.tipErrorKey = '';
    if (sameTipChoice(choice, this.selectedTip)) return;
    // Shown at once; the server's answer (CartService.tip$) then confirms or corrects it.
    this.selectedTip = choice;
    this.cartService.applyTip(choice);
  }

  /** The tip the server says is on the order, or 0. */
  private serverTipOr(res: any): number {
    return parseOrderTip(res)?.amount ?? 0;
  }

  /** The delivery fee the server says is on the order: undefined for none, 0 for free. */
  private serverDeliveryFeeOr(res: any): number | undefined {
    return parseOrderDeliveryFee(res) ?? undefined;
  }

  private serverDiscountOr(res: any): number {
    const parsed = parseFloat(res?.discount_total);
    return isNaN(parsed) || parsed <= 0 ? 0 : parsed;
  }

  /** The code the server says is on the order, or undefined. Never what was typed. */
  private serverPromoCodeOr(res: any): string | undefined {
    const code = res?.promo_code;
    return typeof code === 'string' && code !== '' ? code : undefined;
  }

  /**
   * The server refused a code that was ALREADY applied, so the total has gone up.
   *
   * Not an inline field error: the guest has seen a lower number and may have decided to
   * order because of it. They get told what happened and choose. By the time this runs the
   * cart total is already the corrected one, so the modal shows the truth.
   */
  private onPromoCodeRefused(refusal: PromoCodeRefusal): void {
    this.setPromoCodeRefusal(refusal.reason, refusal.minimumOrderAmount);
    // Nothing is pending — the cart already re-priced itself — so this only needs telling.
    this.promoCodeRefusedBlockedSubmit = false;
    this.promoCodeRefusedTotal = this.total;
    this.showPromoCodeRefusedConfirm = true;
    // Empty for a refused code; the code itself when its discount was only lowered — the
    // response has already set appliedPromoCode either way.
    this.promoCodeInput = this.appliedPromoCode ?? '';
  }

  /** Dismiss the price-went-up notice. The corrected total stands either way. */
  acknowledgePromoCodeRefused(): void {
    this.showPromoCodeRefusedConfirm = false;
    this.promoCodeRefusedKey = '';
  }

  private setPromoCodeError(reason: string | undefined, minimumOrderAmount?: number): void {
    this.promoCodeErrorKey = this.promoCodeReasonKey(reason, minimumOrderAmount);
    this.promoCodeErrorParams = minimumOrderAmount !== undefined
      ? { amount: minimumOrderAmount.toFixed(2) }
      : undefined;
  }

  /** Same as setPromoCodeError(), for the price-went-up dialog. */
  private setPromoCodeRefusal(reason: string | undefined, minimumOrderAmount?: number): void {
    this.promoCodeRefusedKey = this.promoCodeReasonKey(reason, minimumOrderAmount);
    this.promoCodeRefusedParams = minimumOrderAmount !== undefined
      ? { amount: minimumOrderAmount.toFixed(2) }
      : undefined;
  }

  /**
   * Map a server reason to an i18n key.
   *
   * ⚠️ The fallback is not optional. An unmapped reason — a newer backend, a reason added
   * later — would otherwise render an empty box, which reads as the button having silently
   * failed. `generic` says something true about every case.
   */
  private promoCodeReasonKey(reason: string | undefined, minimumOrderAmount?: number): string {
    switch (reason) {
      case 'promo_code_not_found': return 'notFound';
      case 'promo_code_inactive': return 'notFound';
      case 'promo_code_not_started': return 'notStarted';
      case 'promo_code_expired': return 'expired';
      case 'promo_code_used_up': return 'usedUp';
      case 'promo_code_changed': return 'changed';
      // Never "spend at least  to use this code": without the figure, say it without one.
      case 'promo_code_minimum_not_met':
        return minimumOrderAmount !== undefined ? 'minimumNotMet' : 'minimumNotMetNoAmount';
      default: return 'generic';
    }
  }

  /**
   * Make the server's cart the one on screen before anything is ordered.
   *
   * ⚠️ The order used to go out while edits still sat in the sync debounce. The web submit
   * carries no lines, so the server checked the promo code against the cart it had — the
   * one from BEFORE the edit — and placed the order at that price. The order button is held
   * while the cart syncs, so a tap normally finds nothing to wait for; this is the backstop
   * for a tap that lands as the hold lifts, and for edits made behind the payment sheet.
   *
   * @returns false when the order must not go ahead: the sync failed, or the code was
   *          refused on the way and the guest has a new total to look at first.
   */
  private async settleCartBeforeOrder(): Promise<boolean> {
    if (!(await this.cartService.flushPendingSync())) {
      alert(this.translate.instant('orderDetails.orderNow.syncFailed'));
      return false;
    }

    // Opened synchronously by promoCodeRefused$ during that sync, with the corrected total.
    return !this.showPromoCodeRefusedConfirm;
  }

  /** The "tell me when the venue accepts" ask, opened from Order (see askForOrderUpdates). */
  isOrderPushPromptOpen = false;
  private resolveOrderPushPrompt?: () => void;

  /**
   * Asks, once, whether the guest wants to hear when the venue accepts their order, and resolves
   * only when they have answered — the browser's own permission dialog included. Either answer goes
   * on to the order; this only decides whether the order carries a push token.
   */
  private askForOrderUpdates(): Promise<void> {
    if (!this.guestPushService.shouldAskForOrderUpdates()) return Promise.resolve();
    this.guestPushService.markOrderAskShown();
    this.isOrderPushPromptOpen = true;
    return new Promise(resolve => (this.resolveOrderPushPrompt = resolve));
  }

  /** Allowed, refused, or "Not now" — the order goes ahead in every case. */
  onOrderPushPromptClosed(): void {
    this.isOrderPushPromptOpen = false;
    this.resolveOrderPushPrompt?.();
    this.resolveOrderPushPrompt = undefined;
  }

  private async processOrder(): Promise<void> {
    // Nothing should be pending by now — the cart was settled when Order was tapped — but
    // the payment sheet sat in between, and this is the last point before the submit.
    if (!(await this.settleCartBeforeOrder())) return;

    // Check for unavailable items before submitting
    const unavailable = this.cartService.getUnavailableCartItems();
    if (unavailable.length > 0) {
      this.unavailableItemNames = unavailable.map(u => u.name);
      this.unavailableItemProductIds = unavailable.map(u => u.productId);
      this.showUnavailableItemsConfirm = true;
      return;
    }

    // Track order initiated event
    const currentOrderId = this.cartService.getCurrentOrderId();
    const itemCount = this.cartItems.reduce((count, item) => count + item.quantity, 0);

    this.analytics.log('order_initiated', {
      item_count: itemCount,
      order_id: currentOrderId
    });

    // Refresh ordering status before placing order
    await this.refreshOrderingStatus();

    // Match iOS canOrderNow: is_ordering_active must be true (nil/false → false), is_open_now nil = allow
    const canOrderNow = this.clientData?.is_ordering_active === true && (this.clientData?.is_open_now !== false);
    if (!canOrderNow) {
      this.isOrderingUnavailableModalOpen = true;
      return;
    }

    // After every check that can stop the order and before the spinner, so the question only comes
    // up for an order that is really about to go. The order waits here for the answer.
    await this.askForOrderUpdates();

    this.isLoading = true;

    // The status push goes to this token. Started now so it is fetched alongside the region lookup;
    // null (no permission, or too slow) still places the order, just without the push.
    const pushToken = this.guestPushService.orderPushToken();

    // Get region info for dine-in orders
    let regionInfo: any = {};
    const tableIdentifier = sessionStorage.getItem('tableIdentifier');
    if (tableIdentifier) {
      regionInfo = await this.getTableRegionInfo(tableIdentifier);
    }

    // Build order metadata
    const orderMetadata: OrderMetadata = {
      orderingType: this.orderingType,
      orderNote: this.orderNote,
      language: this.languageService.getCurrentLang(),
      pushToken: (await pushToken) ?? undefined
    };

    // Add payment method: dine-in uses selectedPaymentMethod, delivery/pickup uses form data
    if (this.orderingType === 'dine-in' && this.selectedPaymentMethod) {
      orderMetadata.paymentMethod = this.selectedPaymentMethod;
    } else if (this.deliveryFormData?.paymentMethod) {
      orderMetadata.paymentMethod = this.deliveryFormData.paymentMethod;
    }

    // Add type-specific metadata
    if (this.orderingType === 'dine-in') {
      orderMetadata.tableId = sessionStorage.getItem('tableId') || undefined;
      orderMetadata.tableIdentifier = regionInfo.id;
      orderMetadata.regionId = regionInfo.regionId;
      orderMetadata.regionName = regionInfo.regionName;
      orderMetadata.tableName = regionInfo.tableName;
    } else if (this.orderingType === 'delivery' || this.orderingType === 'off-site') {
      if (!this.deliveryFormData) {
        console.error('Delivery form data is missing');
        this.isLoading = false;
        alert('Please fill in the delivery form');
        return;
      }
      
      orderMetadata.deliveryType = this.deliveryFormData.deliveryType;
      orderMetadata.phone = this.deliveryFormData.phone;
      orderMetadata.communicationPreference = this.deliveryFormData.communicationPreference;
      
      if (this.deliveryFormData.deliveryType === 'delivery') {
        orderMetadata.deliveryAddress = this.deliveryFormData.deliveryAddress;
        orderMetadata.locationId = sessionStorage.getItem('locationId') || undefined;
      } else if (this.deliveryFormData.deliveryType === 'pickup') {
        orderMetadata.pickupLocationId = this.deliveryFormData.pickupLocationId;
        orderMetadata.pickupLocationName = this.deliveryFormData.pickupLocationName;
        orderMetadata.pickupLocationAddress = this.deliveryFormData.pickupLocationAddress;
        orderMetadata.locationId = sessionStorage.getItem('locationId') || undefined;
      }
    }

    // Validate order metadata
    const validation = this.orderPayloadService.validateOrderMetadata(orderMetadata);
    if (!validation.isValid) {
      console.error('Order validation failed:', validation.error);
      this.isLoading = false;
      alert(validation.error || 'Invalid order data. Please check your information.');
      return;
    }

    // Build order payload
    const orderPayload = this.orderPayloadService.buildOrderPayload(orderMetadata);

    const orderId = this.cartService.getCurrentOrderId();
    if (orderId) {
      this.apiService.finishOrder(this.clientData.id, orderId, orderPayload).subscribe({
        next: async (res) => {
          /*
           * ⚠️ The venue must NOT be told about an order that has not been paid for.
           *
           * The backend deliberately holds a card order back — WooCommerce at native
           * 'pending', Hype at AWAITING_PAYMENT — so that it appears in neither tab of
           * the BarApp feed until BORICA confirms. Pushing a notification here would
           * defeat that entirely: the kitchen would start on an order the guest may
           * abandon at the payment page, and abandoned orders ARE routine.
           *
           * ⚠️ THE HOLD NOW LIVES ON THE BACKEND, which is why nothing is stashed
           * here any more. It records what it would have sent and releases it from
           * markOrderAsPaid(), so a payment confirmed by borica:check-pending after
           * this browser was closed still reaches the venue. Stashing in
           * localStorage could only ever work if the guest came back — and the case
           * where they don't is exactly the one that leaves a paid order unheard.
           *
           * Every payment method is the backend's now (notify_venue in the
           * payload). It is the only side that can see who is on shift, so the push
           * reaches the person actually working the table rather than whoever the
           * table happens to name.
           */
          const isOnlinePayment = orderMetadata.paymentMethod === 'online_payment';

          // The SERVER's total, not the local one. `this.total` is the optimistic figure
          // from before the response landed; on any order the server re-priced — a promo
          // applied, cleared or refused — it is the wrong number to keep forever.
          const placedTotal = this.serverTotalOr(res, this.total);

          // Same discipline as `placedTotal`: the SERVER's figures. A code the server
          // refused between the last sync and submit is not on this order, whatever the
          // local state still says.
          const placedDiscount = this.serverDiscountOr(res);
          const placedPromoCode = this.serverPromoCodeOr(res);
          const placedTip = this.serverTipOr(res);
          const placedDeliveryFee = this.serverDeliveryFeeOr(res);

          const orderHistoryItem: OrderHistoryItem = {
            id: orderId,
            total: placedTotal,
            status: 'processing',
            items: [...this.cartItems],
            timestamp: Date.now(),
            note: this.orderNote,
            discountTotal: placedDiscount,
            promoCode: placedPromoCode,
            tipAmount: placedTip,
            deliveryFee: placedDeliveryFee,
            shopId: this.clientData.id
          };

          const itemCount = this.cartItems.reduce((count, item) => count + item.quantity, 0);
          const cartTotal = placedTotal.toFixed(2);

          /*
           * ⚠️ A CARD ORDER IS NOT AN ORDER YET.
           *
           * History and order_created are deferred to onPaymentConfirmed() alongside the
           * venue push, because an abandoned card payment is routine: the guest reaches
           * BORICA's page and closes it. Recording 'processing' here would leave the app
           * showing an order that never existed and inflate every funnel that counts it.
           *
           * order_submitted stays here on purpose, for BOTH paths. It means submitted,
           * not paid — order_paid already carries revenue — and iOS and Android fire it
           * at placement too. Moving it would make one event mean different things on
           * different platforms, which is worse than counting an abandonment.
           */
          this.analytics.log('order_submitted', {
            order_id: orderId,
            item_count: itemCount,
            cart_total: cartTotal
          });

          if (!isOnlinePayment) {
            this.orderHistoryService.addOrder(orderHistoryItem);
            this.analytics.log('order_created', {
              order_id: orderId,
              item_count: itemCount
            });
          }

          /*
           * The snapshot, written BEFORE the cart is cleared.
           *
           * The cart genuinely has to go — the order is closed server-side and a live
           * currentOrderId would sync later edits into it. But clearing it is what left
           * every failed payment on an empty screen with a modal and no way back to an
           * order the guest still owes for. So the items survive as a read-only record.
           *
           * Contact details ride along because deliveryFormData is a component field and
           * does not survive the round trip; without them a retry's M_INFO carries a name
           * and nothing else, and the issuer is likelier to challenge or decline it.
           */
          if (isOnlinePayment && orderId) {
            this.awaitingPayments.write({
              orderId,
              shopId: this.clientData.id,
              items: [...this.cartItems],
              total: placedTotal,
              note: this.orderNote,
              currencySymbol: this.currencySymbol,
              // Carried so onPaymentConfirmed() can put them on the history entry it
              // builds from this snapshot — the cart is gone by then.
              discountTotal: placedDiscount,
              promoCode: placedPromoCode,
              tipAmount: placedTip,
              deliveryFee: placedDeliveryFee,
              orderingType: this.orderingType,
              tableContext: this.orderingType === 'dine-in'
                ? {
                    tableId: sessionStorage.getItem('tableId') || '',
                    tableName: regionInfo?.tableName || '',
                    regionId: regionInfo?.regionId || '',
                    regionName: regionInfo?.regionName || '',
                  }
                : null,
              customerPhone: this.deliveryFormData?.phone || undefined,
              placedAt: Date.now(),
            });
          }

          // Forget the order id in the SERVICE, not just in storage — a stale field is
          // what let a later edit land on the finished, still-payable order.
          this.cartService.clearCurrentOrder();
          this.orderNote = '';
          // Clear cart locally without syncing since order is already finished
          this.cartService.clearCartLocally();

          // Check if online payment was selected — redirect to BORICA
          if (isOnlinePayment && orderId) {
            this.initiateOnlinePayment(this.clientData.id, orderId);
            return;
          }

          this.isLoading = false;
          this.isSuccessModalOpen = true;
        },
        error: (err) => {
          console.error('Order error:', err);

          /*
           * ⚠️ A refused promo code is not a failed order, and must not be reported as one.
           *
           * The server answers 409 when the applied code died between Apply and Order. It
           * has deliberately NOT placed the order — nothing reached the venue, no card was
           * charged — because the total went up and the guest has not agreed to the new one.
           * The body is the order at its corrected price, so it can be shown and confirmed.
           *
           * Keyed on the backend `code` rather than on the status: 409 is a general "the
           * state moved under you" answer in this API and reading the status alone would
           * misread an unrelated one as a pricing problem.
           */
          if (err?.status === 409 && err?.error?.code === 'promo_code_refused') {
            this.onPromoCodeRefusedAtSubmit(err.error);
            this.isLoading = false;
            return;
          }

          /*
           * Not a failed order either: the server checked the delivery again at submit and
           * did NOT place it — out of range, below the area's minimum, or no pin. The body is
           * the order with its current figures, so adopt them and put the guest back in the
           * Order-now sheet, which says what is wrong and keeps Place order disabled until it
           * is fixed.
           */
          if (err?.status === 422 && (DELIVERY_SUBMIT_REFUSALS as readonly string[]).includes(err?.error?.code)) {
            this.cartService.adoptRefusedOrder(err.error);
            this.deliverySubmitRefusal = err.error.code;
            this.isLoading = false;
            this.isDeliveryFormOpen = true;
            return;
          }

          // Track order submission failed event
          const failedOrderId = this.cartService.getCurrentOrderId();
          const failedItemCount = this.cartItems.reduce((count, item) => count + item.quantity, 0);
          const failedCartTotal = this.total.toFixed(2);
          const errorMessage = err?.message || err?.error?.message || 'Unknown error';

          this.analytics.log('order_submission_failed', {
            error_message: errorMessage,
            order_id: failedOrderId,
            item_count: failedItemCount,
            cart_total: failedCartTotal
          });

          this.isLoading = false;
          alert('Order failed. Please try again.');
        }
      });
    }
  }


  // ────────────────────────────────────────────────────────────────────────
  // Online payment (BORICA)
  // ────────────────────────────────────────────────────────────────────────

  /**
   * Send the guest to BORICA's hosted payment page.
   *
   * The venue has NOT been notified at this point and the order is held by the
   * backend; both stay that way until onPaymentConfirmed() runs.
   */
  private initiateOnlinePayment(shopId: number, orderId: number): void {
    // BORICA's page is Bulgarian whenever LANG is absent, which is the wrong
    // language for exactly the guests this feature exists for. The gateway knows
    // only BG and EN; the backend narrows whatever we send to those two.
    const language = this.languageService.getCurrentLang();

    /*
     * Delivery orders already carry the phone in billing, which the backend reads.
     * Pickup ones do not — their billing block is omitted on purpose — so send it
     * explicitly and let the backend prefer it. Undefined for dine-in, where no
     * contact detail is collected at all.
     */
    // Falls back to the snapshot, which is the ONLY source on a retry: deliveryFormData
    // is a component field and is null in every component resumed after the round trip —
    // exactly where "pay again" lives. Without it the retry's M_INFO carries a name and
    // nothing else, and the issuer is likelier to challenge or decline it than the first
    // attempt was.
    const record = this.awaitingPayments.read(shopId);
    const customerPhone = this.deliveryFormData?.phone || record?.customerPhone || undefined;

    /*
     * ⚠️ Adopt the record BEFORE going anywhere.
     *
     * On the very first attempt it was written to disk moments ago by processOrder()
     * and this component has never read it back, so `awaitingPayment` is still null.
     * Every failure path below then reasons about a record it cannot see — the
     * retryable branch of onPaymentSettled() reads txCreatedAt off nothing, and
     * applyPaymentUnreachable() cannot tell "no transaction" from "one outstanding".
     */
    if (record && this.awaitingPayment?.orderId !== orderId) {
      this.awaitingPayment = record;
    }

    this.apiService.initiatePayment(shopId, orderId, language, customerPhone).subscribe({
      next: (res) => {
        if (res?.payment_url) {
          /*
           * Remember the TRANSACTION's clock, not ours.
           *
           * The backend reuses a pending transaction for 15 minutes measured from the
           * row's created_at, and a resume hands back the ORIGINAL row. A client timing
           * from its own last call drifts ahead of the server and can ask for a resume
           * the server no longer considers one — minting a second live transaction, and
           * two payable forms, for a single order. rememberedTransaction() owns that
           * distinction; it reads `resumed` so this does not have to.
           *
           * ⚠️ Only patch when there is something to write. patch() is a spread, and a
           * spread copies keys whose value is undefined — so handing it an empty-ish
           * object would ERASE a good timestamp rather than leave it alone.
           */
          const fields = rememberedTransaction(this.awaitingPayments.read(shopId), res);
          if (Object.keys(fields).length > 0) {
            this.awaitingPayments.patch(shopId, fields);
          }

          // A full page navigation, not a router hop: the next screen is BORICA's.
          window.location.href = res.payment_url;
          return;
        }

        /*
         * A 200 with no usable URL. The transaction row may well exist — /pay persists
         * it before it answers — so this is not licence to assert anything about the
         * payment. Ask the backend instead of guessing.
         */
        this.isLoading = false;
        this.isConfirmingPayment = true;
        this.pollPaymentStatus(shopId, orderId, 0, { thorough: false });
      },
      error: (err) => {
        /*
         * The "Order now" spinner is done either way; from here on the busy state that
         * matters is isConfirmingPayment, which dims the panel's own buttons rather
         * than covering the explanation with an overlay.
         */
        this.isLoading = false;
        console.error('Payment initiation failed:', err);

        if (err?.status === 409) {
          /*
           * ⚠️ A 409 IS NEVER ACTED ON DIRECTLY — IT MAY MEAN THE CARD WAS CHARGED.
           *
           * The backend returns 409 for two opposite states and tags them:
           * 'already_paid' means the card WAS charged; 'not_awaiting_payment' means the
           * order moved on. Same status, opposite facts. Rendering "no longer awaiting
           * payment" for the first would tell a guest who has just paid that their
           * order is spent — and skip releasePendingVenueNotification(), the history
           * entry and order_created that onPaymentConfirmed() owns.
           *
           * So ask the backend what actually happened, and only then decide. EVERY 409
           * goes this way, including one whose code we do not recognise or that carries
           * none at all: an unrecognised code is not evidence of either fact.
           *
           * Thorough, because this is precisely the case where we must not guess.
           */
          this.isConfirmingPayment = true;
          this.pollPaymentStatus(shopId, orderId, 0, {
            thorough: true,
            refusal: parseInitiationRefusal(err?.error?.code),
          });
          return;
        }

        if (err?.status === 400 || err?.status === 403) {
          // The terminal pair: the terminal's currency does not match, or this venue
          // cannot take cards at all. Another attempt can only fail identically, which
          // is why this gets its own copy rather than "please try again".
          this.settleOutstandingPanel(shopId, 'unpayable', 'unavailableHere');
          return;
        }

        if (err?.status === 404) {
          /*
           * ⚠️ NOT terminal, and nothing is deleted. /pay 404s whenever resolveOrder()
           * cannot READ the order, and WoocommerceModel::getOrder() catches every
           * exception, so a brief WordPress hiccup is indistinguishable from a
           * deletion. Deleting the guest's only copy of a still-payable order over a
           * transient fault is not a trade worth making.
           */
          this.applyPaymentNotFound(shopId);
          return;
        }

        /*
         * A transport failure or a 5xx. This does NOT tell us the state of the payment:
         * the transaction row is persisted BEFORE the response is sent, so a timeout, a
         * 502 after commit or a response lost on the wire all leave a real pending
         * transaction behind. So we do not guess — we ask.
         */
        this.isConfirmingPayment = true;
        this.pollPaymentStatus(shopId, orderId, 0, { thorough: false });
      }
    });
  }

  /**
   * Handle the return from BORICA.
   *
   * ⚠️ The `payment` query param is USER-CONTROLLED — anyone can type
   * `?payment=success&order=17` — so it is treated as a hint about which order to
   * ask about and nothing more. The answer comes from /payment-status, which
   * triggers a live TRTYPE=90 check when the row is still pending.
   */
  private resumePaymentFromRedirect(): void {
    const shopId = this.clientData?.id;
    const record = shopId ? this.awaitingPayments.read(shopId) : null;
    const returnedOrderId = parseReturnedOrderId(this.route.snapshot.queryParams['order']);

    if (this.route.snapshot.queryParams['order'] !== undefined) {
      // Drop the params immediately so a refresh — or the browser's back button —
      // cannot replay this, and so the URL stops carrying a payment claim.
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    }

    /*
     * ⚠️ THE RECORD, NOT THE URL, IS WHAT DECIDES THERE IS SOMETHING TO RESUME.
     *
     * Requiring `?order=` covered only the guest who came back the tidy way. The
     * commonest failure by far is abandonment — closing BORICA's tab — and that guest
     * returns with no params at all, so the old guard returned immediately and left
     * them on an empty cart with no explanation and an order still held server-side.
     *
     * shopId also comes from the record where possible: `clientData` is read from
     * sessionStorage, which is per-tab, and some issuers' ACS pages open a new one.
     */
    const resumeShopId = record?.shopId ?? shopId;
    const orderId = record?.orderId ?? returnedOrderId;

    if (!resumeShopId || orderId === null || orderId === undefined) {
      return;
    }

    this.awaitingPayment = record;

    if (record) {
      this.restoreOrderingContext(record);
    }

    /*
     * A settled record has already had a terminal answer from the backend. Render what
     * it said and stop — re-polling a dead order on every boot for a day is pointless
     * traffic, and the answer cannot change.
     */
    if (record?.settled) {
      this.applyPanel(record.settled.phase, record.settled.messageKey);
      return;
    }

    /*
     * ⚠️ Paint BEFORE polling.
     *
     * The burst below can take ~12s when the row is still pending — the normal case on
     * arrival, since BORICA's callback and the guest's own browser race each other back.
     * Until the panel exists, `showingOutstandingPanel` is false and the loading overlay
     * covers the whole viewport, so the guest sat in front of an opaque spinner for all
     * of it with nothing to read and nothing to press. Showing "still being confirmed"
     * first costs nothing: the burst carries on underneath and repaints when it resolves.
     */
    this.showOutstandingPanel('confirming', 'stillProcessing');

    this.isConfirmingPayment = true;
    this.pollPaymentStatus(resumeShopId, orderId, 0, { thorough: true });
  }

  // ── The outstanding-payment panel ───────────────────────────────────────

  /**
   * Put back the ordering context the return trip destroyed.
   *
   * ⚠️ BORICA's return URL carries no table or location params, so Splash re-derives
   * orderingType as 'off-site' and OVERWRITES the stored value. A dine-in guest coming
   * back from a failed payment is then treated as a delivery customer: pushed at the
   * delivery form and gated on ft_orders_from_home, for an order they placed at a table.
   *
   * The snapshot is the only thing that still knows, which is why it carries them.
   */
  private restoreOrderingContext(record: AwaitingPaymentOrder): void {
    try {
      if (record.orderingType) {
        sessionStorage.setItem('orderingType', record.orderingType);
        this.orderingType = record.orderingType as typeof this.orderingType;
      }

      if (record.tableContext?.['tableId']) {
        sessionStorage.setItem('tableId', record.tableContext['tableId']);
      }
    } catch {
      /* storage denied — the panel still renders, the guest just may be re-routed */
    }
  }

  /** Show the panel for a phase, leaving the record alone. */
  private showOutstandingPanel(
    phase: PaymentPhase,
    messageKey: string,
    params?: Record<string, string>
  ): void {
    /*
     * The record's own shopId wins over clientData.
     *
     * clientData is read from sessionStorage, which is per-tab — and an issuer's ACS
     * page opening a new tab is precisely one of the journeys this panel exists to
     * survive. Reading through clientData there would return null, blank
     * `awaitingPayment`, and the panel would silently not render even though we had
     * just resumed from a perfectly good record.
     */
    const shopId = this.awaitingPayment?.shopId ?? this.clientData?.id;
    if (shopId) {
      this.awaitingPayment = this.awaitingPayments.read(shopId) ?? this.awaitingPayment;
    }

    this.applyPanel(phase, messageKey, params);
  }

  /**
   * Record a TERMINAL answer and show it.
   *
   * ⚠️ Settling KEEPS the record. The first design deleted it on a terminal answer,
   * which put abandonment — whose terminus is 'expired' — straight back on the empty
   * screen this whole feature exists to remove. Deleting is for success, for an
   * explicit "start a new order", and for the 24-hour sweep of already-settled rows.
   */
  private settleOutstandingPanel(shopId: number, phase: PaymentPhase, messageKey: string): void {
    this.awaitingPayment = this.awaitingPayments.settle(shopId, phase, messageKey);
    this.applyPanel(phase, messageKey);
  }

  /**
   * Keys whose MEANING changed, mapped forward for records settled by an older build.
   *
   * ⚠️ `contactVenue` used to be the unreachable sentence and is now the call button's
   * label ("Call the venue"). A settled record lives for 24 hours, so without this a
   * guest who hit the 404 terminus just before the deploy would come back to a panel
   * whose entire explanation reads "Call the venue" — in a phase that shows no call
   * button either. That is the exact defect this rename exists to prevent, arriving by
   * way of localStorage instead of a copy-paste.
   */
  private static readonly LEGACY_MESSAGE_KEYS: Record<string, string> = {
    contactVenue: 'serviceUnreachable',
  };

  /** Compute what the banner says and which buttons it offers. */
  private applyPanel(
    phase: PaymentPhase,
    messageKey: string,
    params?: Record<string, string>
  ): void {
    const key = OrderDetailsComponent.LEGACY_MESSAGE_KEYS[messageKey] ?? messageKey;

    this.panelPhase = phase;
    // Both spellings are kept: the template binds the prefixed one, while the store's
    // settled.messageKey holds the bare one — startNewOrder() needs it to settle with.
    this.panelMessageKeyRaw = key;
    this.panelMessageKey = 'orderDetails.payment.' + key;
    this.panelMessageParams = params;

    const record = this.awaitingPayment;
    const now = Date.now();

    /*
     * "Continue payment" resumes an abandoned attempt, and is offered ONLY inside the
     * server's 15-minute reuse window. Outside it, /pay would mint a second live
     * transaction against the same order — two payable forms, one order.
     *
     * This is the case that had no way out at all before: the guest who closed BORICA's
     * tab saw "still processing" for up to thirty minutes and then watched the order be
     * cancelled underneath them.
     */
    this.canContinuePayment =
      phase === 'confirming' && withinResumeWindow(record?.txCreatedAt, now);

    /*
     * "Pay again" is for a finished attempt that failed, and 'retryable' now means
     * exactly that and nothing else.
     *
     * ⚠️ The 60-minute payable window is NOT checked here. It is checked in
     * onPaymentSettled(), before the phase is chosen, because a decline that has aged
     * out is not a retryable state with the button hidden — it is a different state,
     * with its own wording, and deciding that after the fact left the panel saying
     * "your card was not charged" beside no way to act on it.
     */
    this.canPayAgain = phase === 'retryable';
  }

  /**
   * Ask the backend what happened, and keep asking while it says 'pending'.
   *
   * Pending is a real state here, not an error: BORICA's callback and the guest's
   * own browser race each other back, and the guest usually wins. The backend
   * escalates to a live status check on its side once the row is 20s old, so a few
   * spaced attempts is enough to turn a pending row into a real answer.
   */
  private pollPaymentStatus(
    shopId: number,
    orderId: number,
    attempt: number,
    opts: { thorough: boolean; refusal?: InitiationRefusal | null } = { thorough: true }
  ): void {
    const MAX_ATTEMPTS = 5;
    const RETRY_DELAY_MS = 2500;

    this.apiService.getPaymentStatus(shopId, orderId).subscribe({
      next: (status) => {
        if (this.destroyed) {
          return;
        }

        /*
         * A quick read paints the panel and stops. Only a thorough poll waits out a
         * pending row — which is what the return from BORICA and a 409 both need, and
         * what "Check again" deliberately does not: the guest is already looking at a
         * panel with that button on it, and holding them through a twelve-second burst
         * to repaint it makes a working screen look broken.
         */
        if (opts.thorough && shouldRetryPaymentStatus(status?.status, attempt, MAX_ATTEMPTS)) {
          this.paymentPollTimer = setTimeout(
            () => this.pollPaymentStatus(shopId, orderId, attempt + 1, opts),
            RETRY_DELAY_MS
          );
          return;
        }

        this.isConfirmingPayment = false;

        // A successful answer clears any 404 streak — the backend is plainly reachable.
        this.awaitingPayments.patch(shopId, { notFoundCount: 0 });

        if (status?.status === 'success') {
          // At ANY age. A payment the browser never saw confirmed still has to reach
          // the history and the venue, however long it took the guest to come back.
          this.onPaymentConfirmed(orderId, status);
          return;
        }

        this.onPaymentSettled(shopId, orderId, status, opts.refusal ?? null);
      },
      error: (err) => {
        if (this.destroyed) {
          return;
        }

        /*
         * ⚠️ A 404 HERE DOES NOT MEAN THE ORDER IS GONE.
         *
         * /payment-status 404s when no BoricaTransaction row exists — which is exactly
         * the "/pay never started" case: the order is placed, held server-side and still
         * fully payable. Deleting the record on it would destroy the only handle the
         * guest has on an order they can still pay for, and no sweep would ever rescue
         * it, because every sweep iterates transaction rows and there is none.
         */
        if (err?.status === 404) {
          this.isConfirmingPayment = false;
          this.applyPaymentNotFound(shopId);
          return;
        }

        /*
         * ⚠️ A FAILED REQUEST IS RETRIED TOO.
         *
         * This used to give up on the first one — and a failed request answers nothing,
         * which is not 'pending', so a single dropped packet ended the poll and the
         * guest was told "we couldn't confirm your payment". At a table on weak wifi
         * that is the common case, not the rare one.
         */
        if (opts.thorough && attempt < MAX_ATTEMPTS) {
          this.paymentPollTimer = setTimeout(
            () => this.pollPaymentStatus(shopId, orderId, attempt + 1, opts),
            RETRY_DELAY_MS
          );
          return;
        }

        this.isConfirmingPayment = false;

        console.error('Payment status check failed:', err);
        this.applyPaymentUnreachable(opts.refusal ?? null);
      }
    });
  }

  /**
   * A 404 from /payment-status, counted across boots.
   *
   * ⚠️ Nothing here deletes anything — see pollPaymentStatus. notFoundOutcome() owns the
   * decision; this only persists the streak and renders what it returns.
   */
  private applyPaymentNotFound(shopId: number): void {
    const record = this.awaitingPayments.read(shopId);
    const seen = (record?.notFoundCount ?? 0) + 1;
    this.awaitingPayments.patch(shopId, { notFoundCount: seen });

    if (!record) {
      // No record either — a hand-typed ?order=. Nothing to report, and no panel to
      // paint it on.
      return;
    }

    const outcome = notFoundOutcome(record, seen, Date.now());

    if (outcome.settle) {
      this.settleOutstandingPanel(shopId, outcome.phase, outcome.messageKey);
      return;
    }

    this.showOutstandingPanel(outcome.phase, outcome.messageKey);
  }

  /**
   * No answer at all from /payment-status.
   *
   * ⚠️ Never settles and never deletes: this says nothing about the money either way.
   * The record survives untouched and the next boot asks again.
   */
  private applyPaymentUnreachable(refusal: InitiationRefusal | null): void {
    const outcome = unreachableOutcome(refusal, this.awaitingPayment?.txCreatedAt);

    this.showOutstandingPanel(outcome.phase, outcome.messageKey);
  }

  /**
   * A non-success answer from the backend: decide the phase and whether it is final.
   *
   * ⚠️ Only statuses the BACKEND returned SETTLE a record. The one clock consulted here
   * — the 60-minute payable window on a decline — deliberately does not settle what it
   * decides, precisely because a clock said it and not the backend.
   */
  private onPaymentSettled(
    shopId: number,
    orderId: number,
    status: PaymentStatusResponse | null,
    refusal: InitiationRefusal | null = null
  ): void {
    // ⚠️ String, never a number. RC is alphanumeric and '1A' is not 1.
    const rc = status?.rc_code === null || status?.rc_code === undefined
      ? null
      : String(status.rc_code);

    const phase = paymentPhase(status?.status, rc);

    /*
     * Spec Table 2: "ATTENTION! E-merchant must display the value to the cardholder."
     *
     * ⚠️ Captured for EVERY phase, not only success. A refusal is the case the
     * obligation was written for — the issuer's ACS sends this to explain why — and
     * reading it only on the success path dropped the one message it asked us to pass on.
     */
    if (status?.cardholder_info) {
      this.paymentCardholderInfo = status.cardholder_info;
    }

    // A success never reaches here — pollPaymentStatus routes it to onPaymentConfirmed —
    // but it is named anyway, so that a future caller cannot log an approval as a failure.
    if (phase !== 'confirming' && phase !== 'paid') {
      // ⚠️ The RC stays a STRING all the way here. '1A' is a real soft decline and is
      // not 1. Without this event there is no way to see a decline rate at all.
      this.analytics.log('order_payment_failed', {
        order_id: orderId,
        rc_code: rc?.trim() ?? ''
      });
    }

    if (phase === 'confirming') {
      // Undecided rather than refused. borica:check-pending resolves it within minutes
      // either way, so the guest is told to wait — never told it failed, which would be
      // a lie about their money. Not settled: the answer can still change.
      this.showOutstandingPanel('confirming', 'stillProcessing');
      return;
    }

    if (phase === 'retryable') {
      this.onPaymentFailed(orderId, status, rc);

      /*
       * A decline, or a form the guest cancelled. Still payable only while the order
       * behind it is alive — the sweep cancels it 60 minutes after the TRANSACTION was
       * created. A record with no transaction timestamp counts as payable: nothing was
       * ever started, so nothing can have aged out.
       */
      const record = this.awaitingPayment;
      const stillPayable =
        isDeclineStillPayable(record, Date.now())
        || record?.txCreatedAt === null
        || record?.txCreatedAt === undefined;

      if (stillPayable) {
        // paymentAlert() owns the wording: it is what decides when we may say "your card
        // has not been charged" and when we must not. Do not restate that logic here.
        const alert = paymentAlert(status?.status, rc);
        this.showOutstandingPanel('retryable', alert.key, alert.params);
        return;
      }

      /*
       * A decline the guest came back to too late. The sweep released the order an hour
       * after the transaction was created, so "pay again" is withdrawn — but leaving the
       * decline wording next to no button explains nothing. Say what actually happened:
       * the order was released for non-payment. That is a statement about the ORDER, and
       * it never contradicts what we told them about their card.
       *
       * ⚠️ Deliberately NOT settled. The backend never said this order was dead — a clock
       * here did, and a clock is exactly what is not allowed to make that call. Settling
       * would stop every later boot from asking, so a device whose clock ran fast, or a
       * sweep that had not run, would bury a payable order for good.
       */
      this.showOutstandingPanel('unpayable', 'orderReleased');
      return;
    }

    if (phase === 'reversed') {
      // Charged, then refunded. Saying "we could not confirm whether your card was
      // charged" here would be simply false, so it gets its own wording.
      this.settleOutstandingPanel(shopId, 'reversed', 'refunded');
      this.onPaymentFailed(orderId, status, rc);
      return;
    }

    if (phase === 'unpayable') {
      // 'expired', or a 409 that turned out to be 'not_awaiting_payment' — the order
      // moved on, which is a different sentence from a payment window closing.
      const key = expiredMessageKey(refusal);
      this.settleOutstandingPanel(shopId, 'unpayable', key);
      this.onPaymentFailed(orderId, status, rc);
      return;
    }

    // 'unconfirmed'. The one outcome where the guest may genuinely have been charged,
    // so it is settled but the record is kept and the panel shows the order number.
    // A Back-to-shop cancel is NOT this: that is RC -25 and lands in 'retryable'.
    this.settleOutstandingPanel(shopId, 'unconfirmed', 'unconfirmed');
    this.onPaymentFailed(orderId, status, rc);
  }

  /** The money is confirmed: release the venue notification, then celebrate. */
  private onPaymentConfirmed(orderId: number, status: PaymentStatusResponse): void {
    // The venue is told by the backend, from markOrderAsPaid() — see finishOrder().

    /*
     * The order becomes real HERE, not at placement.
     *
     * finishOrder() deliberately skipped the history entry and order_created for card
     * orders — an abandoned payment would otherwise leave the app showing an order that
     * never happened. This is the other half of that, and it must run on ANY boot that
     * learns the payment succeeded, however late: a guest whose confirmation never
     * arrived is charged, is being served, and would otherwise see nothing at all.
     *
     * Built from the snapshot because the cart was cleared before the redirect, and
     * written BEFORE the modal opens — its "view details" button reads orderHistory[0].
     */
    const shopId = this.awaitingPayment?.shopId ?? this.clientData?.id;
    const record = shopId ? this.awaitingPayments.read(shopId) : null;

    if (record && record.orderId === orderId) {
      // Guard against a second tab having already done this.
      if (!this.orderHistoryService.getOrderById(orderId, shopId)) {
        this.orderHistoryService.addOrder({
          id: orderId,
          total: record.total,
          status: 'processing',
          items: record.items || [],
          timestamp: record.placedAt || Date.now(),
          note: record.note || '',
          // Straight off the snapshot — the cart that knew about the code was cleared
          // before the redirect, so this is the only place left that remembers.
          discountTotal: record.discountTotal,
          promoCode: record.promoCode,
          tipAmount: record.tipAmount,
          deliveryFee: record.deliveryFee,
          shopId: Number(shopId)
        });

        this.analytics.log('order_created', {
          order_id: orderId,
          item_count: (record.items || []).reduce((count, item) => count + item.quantity, 0)
        });
      }

      // Paid and accounted for: this is one of the few things that may delete it.
      this.awaitingPayments.clear(record.shopId);
    }

    this.awaitingPayment = null;
    this.panelPhase = 'none';

    // Spec Table 2: "E-merchant must display the value to the cardholder."
    this.paymentCardholderInfo = status?.cardholder_info || null;

    this.analytics.log('order_paid', {
      order_id: orderId,
      payment_method: 'online_payment'
    });

    this.isSuccessModalOpen = true;
  }

  /**
   * Declined, errored or expired.
   *
   * ⚠️ status.rc_code stays a STRING all the way to the screen. RC is alphanumeric
   * and a soft-declined VISA answers '1A'; parsed as a number it becomes 1, which
   * means something entirely different.
   */
  private onPaymentFailed(
    orderId: number,
    status: PaymentStatusResponse | null,
    rc: string | null = null
  ): void {
    /*
     * ⚠️ THE STASHED VENUE PUSH MUST SURVIVE A DECLINE.
     *
     * This used to clear it unconditionally, on the belief — stated in a comment
     * here, and wrong — that the backend sweep cancels the held order anyway. It does
     * not: borica:check-pending only ever looks at transactions still PENDING, so an
     * order behind a DECLINED one is never touched. It stays held and stays payable.
     *
     * Which is what makes clearing it dangerous now that a decline can be retried:
     * decline → stash deleted → guest retries → pays → onPaymentConfirmed() finds
     * nothing to release → the venue is never told about an order it has been paid
     * for. And it fails silently, because the backend still promotes the order into
     * the feed. Only a definitively dead order releases the stash.
     */
    if (status?.status === 'expired') {
      this.clearPendingVenueNotification(orderId);
    }

    // Still payable, so offer the way back — a decline, or a form the guest cancelled.
    // Anything else is not retryable: an expired session is gone, and an unclassified
    // outcome must not invite a second charge when we cannot say whether the first one
    // landed.
    this.paymentRetryOrderId = isRetryablePayment(status?.status, rc) ? orderId : null;

    /*
     * No message and no analytics from here any more.
     *
     * What the guest is told lives on the outstanding-payment panel, set by
     * onPaymentSettled() — which shows the items alongside it. The modal this used to
     * open appeared over an empty cart and left nothing behind when dismissed, which
     * is the whole failure this work exists to fix. order_payment_failed moved there
     * too, so it is logged once per answer, from the one place that knows the phase.
     *
     * This method keeps only the parts that are not display: the venue-push lifecycle
     * and whether a retry may be offered at all.
     */
  }

  // ── The deferred venue notification ─────────────────────────────────────

  /**
   * ⚠️ THE VENUE'S ORDER PUSH IS NOT SENT FROM HERE ANY MORE.
   *
   * buildVenueNotification(), sendVenueNotification() and the localStorage stash
   * that carried a card order across the BORICA redirect all lived here. The
   * backend announces every order now — at submit for cash and card-at-table, and
   * from markOrderAsPaid() for an online payment — because it is the only side
   * that can see which staff member is on shift, and the only one still there when
   * the guest never comes back from the payment page.
   *
   * Waiter calls and bill requests are still sent by this app, from
   * NotificationService, and still go to whoever the table names.
   */


  private clearPendingVenueNotification(orderId: number): void {
    try {
      localStorage.removeItem(this.pendingNotificationKey(orderId));
    } catch { /* best effort */ }
  }

  private pendingNotificationKey(orderId: number): string {
    return `pendingOrderNotification_${orderId}`;
  }

  // ── The app's normal error UI, in place of alert() ───────────────────────


  /**
   * Pay for the same held order again, after a decline.
   *
   * Goes back through /pay rather than reusing the old transaction: a declined row is
   * not pending, so the backend allocates a fresh ORDER and a new transaction for the
   * same order — which is what BORICA requires of a second attempt.
   *
   * The offer is withdrawn before the call, so a slow network cannot produce two
   * payment sessions from two taps.
   */
  retryPayment(): void {
    const shopId = this.panelShopId;
    const orderId = this.awaitingPayment?.orderId ?? this.paymentRetryOrderId;

    if (orderId === null || orderId === undefined || !shopId) {
      return;
    }

    // Withdrawn BEFORE the call, so a slow network cannot turn two taps into two
    // payment sessions.
    this.paymentRetryOrderId = null;
    this.canPayAgain = false;
    this.isLoading = true;

    this.initiateOnlinePayment(shopId, orderId);
  }

  /**
   * Resume an abandoned attempt — the guest who closed BORICA's page and came back.
   *
   * ⚠️ Safe ONLY inside the server's 15-minute reuse window, which is why applyPanel()
   * gates the button on withinResumeWindow() against the TRANSACTION's created_at.
   * Inside it, /pay hands back a signed URL for the very same transaction. Outside it,
   * /pay mints a second one and the guest ends up with two live payment forms for one
   * order — so the button is simply not offered rather than being offered and refused.
   *
   * This is the case that previously had no way out at all: "still processing" for up
   * to thirty minutes, then the order cancelled underneath them.
   */
  continuePayment(): void {
    const shopId = this.panelShopId;
    const orderId = this.awaitingPayment?.orderId;

    if (!shopId || orderId === undefined) {
      return;
    }

    this.canContinuePayment = false;
    this.isLoading = true;

    this.initiateOnlinePayment(shopId, orderId);
  }

  /**
   * Give up on the outstanding order and go back to the menu.
   *
   * ⚠️ Navigates rather than just clearing. finishOrder() forgot the order id on the
   * way to BORICA and only MenuComponent's createInitialOrder() ever mints one, so
   * revealing the empty cart in place would leave a screen with nothing to add to —
   * items tapped there would be silently dropped.
   */
  startNewOrder(): void {
    const shopId = this.panelShopId;

    if (shopId) {
      /*
       * ⚠️ For 'unconfirmed' and 'reversed' the record is KEPT, settled. Those are the
       * phases where money may have moved, and deleting the items, the amount and the
       * order number at the exact moment the copy says "check with the venue" destroys
       * the only thing the guest has to show them.
       */
      if (this.panelPhase === 'unconfirmed' || this.panelPhase === 'reversed') {
        this.awaitingPayments.settle(
          shopId,
          this.panelPhase,
          this.panelMessageKeyRaw || 'unconfirmed'
        );
      } else {
        this.awaitingPayments.clear(shopId);
      }
    }

    this.awaitingPayment = null;
    this.panelPhase = 'none';
    this.canPayAgain = false;
    this.canContinuePayment = false;

    this.router.navigate(['/menu']);
  }

  /**
   * Re-ask the backend. The only action offered while a payment is still undecided.
   *
   * One round trip, not a burst: the guest is already looking at this panel, and making
   * them wait out five spaced attempts before it repaints makes a working screen look
   * broken. If the answer is still pending, the button is still there.
   */
  checkPaymentAgain(): void {
    const shopId = this.panelShopId;
    const orderId = this.awaitingPayment?.orderId;

    if (!shopId || orderId === undefined || this.isConfirmingPayment) {
      return;
    }

    this.isConfirmingPayment = true;
    this.pollPaymentStatus(shopId, orderId, 0, { thorough: false });
  }

  /**
   * The shop the panel is about.
   *
   * ⚠️ The record's own shopId wins over clientData, which is read from sessionStorage
   * and is therefore per-tab — and an issuer's ACS page opening a new tab is precisely
   * one of the journeys this panel exists to survive. Reading through clientData alone
   * left every button on this panel silently doing nothing in that tab.
   */
  private get panelShopId(): number | undefined {
    return this.awaitingPayment?.shopId ?? this.clientData?.id;
  }

  /**
   * The venue's number for the "Call the venue" button, or null when there is none.
   *
   * A tel: link with no number is worse than no button, so the caller guards on this
   * being non-null rather than rendering an inert link.
   */
  get venuePhone(): string | null {
    const phone = this.clientData?.contact_phone;

    if (typeof phone !== 'string') {
      return null;
    }

    const stripped = phone.replace(/\s+/g, '');

    return stripped === '' ? null : stripped;
  }

  /** The items to show: the live cart normally, the snapshot while a payment is out. */
  get displayItems(): CartItem[] {
    return this.showingOutstandingPanel ? (this.awaitingPayment?.items || []) : this.cartItems;
  }

  /**
   * Whether the panel is taking over the item list.
   *
   * ⚠️ Only while the live cart is EMPTY. The first design rendered the snapshot
   * instead of the cart for as long as a record existed — up to a day — so a guest who
   * declined and then simply added a dish found their new items invisible and
   * unorderable, cash orders included. Any live cart wins.
   */
  get showingOutstandingPanel(): boolean {
    return this.panelPhase !== 'none' && !!this.awaitingPayment && this.cartItems.length === 0;
  }

  /** Read-only rows: no steppers, no tap-to-edit, no "add more" onto a spent order. */
  get readOnlyItems(): boolean {
    return this.showingOutstandingPanel;
  }

  /**
   * A submit is in flight and the order bar should say so itself, iOS-style, instead of
   * the full-viewport overlay covering the screen.
   *
   * ⚠️ `isLoading` is not only set by finishOrder() — payAgain() and continuePayment()
   * set it too. Both of those run only while the outstanding-payment panel is up, which
   * makes `readOnlyItems` true and removes the order bar entirely, so excluding that case
   * leaves exactly the submission this bar is reporting on.
   */
  get isSubmittingOrder(): boolean {
    return this.isLoading && !this.readOnlyItems;
  }

  getSecondPrice(price: number): string | null {
    return this.currencyService.getSecondPrice(price, this.clientData);
  }

  getSecondCurrencySymbol(): string | null {
    return this.currencyService.getSecondCurrencySymbol(this.clientData);
  }

  isSecondCurrencyActive(): boolean {
    return this.currencyService.isSecondCurrencyActive(this.clientData);
  }

  isOnSale(item: CartItem): boolean {
    if (!item.regular_price) return false;
    return item.price < parseFloat(item.regular_price);
  }

}
